<template>
  <div
    class="kv-ve"
    :class="{
      'kv-ve--dragging': isDragging,
      'kv-ve--drag-rows': dragGroup === 'rows',
      'kv-ve--settings-overlay-open': isSettingsOverlayOpen,
      'kv-ve--preview': preview,
      'kv-ve--style-paste': styleClipboardActive,
      'kv-ve--style-paste-row': styleClipboard?.kind === 'row',
      'kv-ve--style-paste-col': styleClipboard?.kind === 'column' || styleClipboard?.kind === 'column-duplicate',
      'kv-ve--style-paste-container': styleClipboard?.kind === 'container',
      'kv-ve--mobile-ui': isMobileUi,
    }"
    @keydown="onLocalKeydown"
  >
    <KvModal
      :modelValue="templateGalleryOpen"
      @update:modelValue="templateGalleryOpen = $event"
      size="large"
      @close="templateGalleryOpen = false"
    >
      <template #title>{{ $_("visualEditor-emailEditor-templateGallery-title") }}</template>
      <template #subtitle>{{ $_("visualEditor-emailEditor-templateGallery-subtitle") }}</template>

      <template #body>
        <div class="kv-ve__template-gallery">
          <div v-if="templateGalleryLoading" class="kv-ve__template-gallery-empty">
            {{ $_("visualEditor-emailEditor-templateGallery-loading") }}
          </div>
          <div v-else class="kv-ve__template-grid">
            <div
              v-for="t in templateGalleryItems"
              :key="t.id"
              class="kv-ve__template-card"
              :class="{ 'kv-ve__template-card--blank': t.__blank }"
              role="button"
              tabindex="0"
              @click="onTemplateGalleryItemClick(t)"
              @keydown.enter.prevent="onTemplateGalleryItemClick(t)"
            >
              <button
                v-if="!t.__blank && canEditSavedAsset('template', t)"
                type="button"
                class="kv-ve__template-edit-btn"
                :title="$_('visualEditor-emailEditor-tooltips-edit')"
                :aria-label="$_('visualEditor-emailEditor-tooltips-edit')"
                @click.stop="openEditSavedAssetModal('template', t)"
              >
                <span class="mdi mdi-pencil" aria-hidden="true"></span>
              </button>
              <div class="kv-ve__template-thumb" aria-hidden="true">
                <div v-if="t.__blank" class="kv-ve__template-thumb-fallback kv-ve__template-thumb-fallback--blank">
                  <span class="mdi mdi-file-outline" aria-hidden="true"></span>
                </div>
                <img v-else-if="t.screenshot" :src="t.screenshot" :alt="t.name" loading="lazy" />
                <div v-else class="kv-ve__template-thumb-fallback">{{ $_("visualEditor-emailEditor-templateGallery-noScreenshot") }}</div>
              </div>
              <div class="kv-ve__template-name">{{ t.name }}</div>
              <div class="kv-ve__template-meta">
                {{ t.__blank ? $_("visualEditor-emailEditor-templateGallery-blankModel") : (t.places_id ? $_("visualEditor-emailEditor-templateGallery-place") : $_("visualEditor-emailEditor-templateGallery-global")) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer="{ toggle }">
        <KvButton variant="outlined" @click="toggle(false)">{{ $_("visualEditor-emailEditor-templateGallery-close") }}</KvButton>
      </template>
    </KvModal>

    <FilesGalleryModal
      v-model="imageGalleryOpen"
      size="large"
      :selectable="true"
      :autoCloseOnSelect="true"
      :allowUpload="true"
      :allowDelete="false"
      type-filter="image"
      @select="onImagePickedFromGallery"
      @close="onImageGalleryClosed"
    />

    <!-- Save / Edit Template or Module -->
    <KvModal
      :modelValue="saveAssetModalOpen"
      @update:modelValue="saveAssetModalOpen = $event"
      size="regular"
      @close="closeSaveAssetModal"
    >
      <template #title>
        {{ saveAssetMode === 'edit' ? $_("visualEditor-emailEditor-saveAsset-editTitle", [saveAssetKindLabel]) : $_("visualEditor-emailEditor-saveAsset-saveAsTitle", [saveAssetKindLabel]) }}
      </template>
      <template #subtitle>
        {{ saveAssetMode === 'edit' ? $_("visualEditor-emailEditor-saveAsset-editSubtitle") : $_("visualEditor-emailEditor-saveAsset-createSubtitle") }}
      </template>

      <template #body>
        <div class="kv-ve__save-asset-form" @click.stop>
          <div class="form-group">
            <label>{{ $_("visualEditor-emailEditor-saveAsset-name") }}</label>
            <input
              v-model="saveAssetName"
              class="form-control"
              type="text"
              maxlength="80"
              :placeholder="$_('visualEditor-emailEditor-saveAsset-namePlaceholder')"
              :disabled="saveAssetSaving"
            />
          </div>

          <div v-if="isGlobalAdmin" class="form-group">
            <label>{{ $_("visualEditor-emailEditor-saveAsset-visibility") }}</label>
            <div class="kv-ve__radio-group">
              <label class="kv-ve__radio">
                <input type="radio" value="place" v-model="saveAssetVisibility" :disabled="saveAssetSaving" />
                <span>{{ $_("visualEditor-emailEditor-saveAsset-placeVisibility") }}</span>
              </label>
              <label class="kv-ve__radio">
                <input type="radio" value="global" v-model="saveAssetVisibility" :disabled="saveAssetSaving" />
                <span>{{ $_("visualEditor-emailEditor-saveAsset-globalVisibility") }}</span>
              </label>
            </div>
          </div>
          <div v-else class="kv-ve__save-asset-hint">
            {{ $_("visualEditor-emailEditor-saveAsset-placeHint") }}
          </div>
        </div>
      </template>

      <template #footer="{ toggle }">
        <div class="kv-ve__save-asset-footer">
          <button
            v-if="saveAssetMode === 'edit' && canDeleteSavedAsset(saveAssetEditingItem)"
            type="button"
            class="btn btn-danger"
            :disabled="saveAssetSaving"
            @click="deleteSavedAsset"
          >
            {{ $_("visualEditor-emailEditor-saveAsset-delete") }}
          </button>
          <div class="kv-ve__save-asset-footer-spacer"></div>
          <KvButton variant="outlined" :disabled="saveAssetSaving" @click="toggle(false); closeSaveAssetModal()">
            {{ $_("visualEditor-emailEditor-saveAsset-cancel") }}
          </KvButton>
          <KvButton
            variant="filled"
            color="primary"
            :disabled="saveAssetSaving || !String(saveAssetName || '').trim()"
            @click="submitSaveAssetModal"
          >
            {{ saveAssetSaving ? $_("visualEditor-emailEditor-saveAsset-saving") : $_("visualEditor-emailEditor-saveAsset-save") }}
          </KvButton>
        </div>
      </template>
    </KvModal>

    <!-- Left Sidebar: Add Elements -->
    <div class="kv-ve__sidebar kv-ve__sidebar--add" v-if="!isMobileUi">
      <div class="kv-ve__tabs">
        <KvTabs v-model="addElementsTab" grow>
          <template #tabs>
            <KvTab value="blocks">{{ $_("visualEditor-emailEditor-tabs-blocks") }}</KvTab>
            <KvTab value="modules">{{ $_("visualEditor-emailEditor-tabs-modules") }}</KvTab>
            <KvTab value="layout">{{ $_("visualEditor-emailEditor-tabs-layout") }}</KvTab>
          </template>
        </KvTabs>
      </div>
      <div class="kv-ve__sidebar-scroll kv-scrollbar">
        <!-- Blocks Tab -->
        <div v-show="addElementsTab === 'blocks'" class="kv-ve__group">
          <!-- AI Commander (in blocks area) -->
          <div class="kv-ve__ai-commander" @click.stop>
            <div class="kv-ve__ai-commander-inner">
              <div class="kv-ve__ai-commander-title">
                <span class="kv-ve__ai-commander-badge">{{ $_("visualEditor-emailEditor-ai-badge") }}</span>
              </div>
              <div class="kv-ve__ai-commander-hint">
                {{ $_("visualEditor-emailEditor-ai-hint") }}
              </div>
              <textarea
                ref="aiPromptTextarea"
                v-model="aiPrompt"
                class="kv-ve__ai-textarea"
                :style="{ height: `${aiPromptHeight}px` }"
                :disabled="aiLoading"
                :placeholder="$_('visualEditor-emailEditor-ai-placeholder')"
                :aria-label="$_('visualEditor-emailEditor-ai-ariaLabel')"
                @input="onAiPromptInput"
                @keydown.ctrl.enter.prevent="submitAiEdit"
                @keydown.meta.enter.prevent="submitAiEdit"
              ></textarea>
              <div class="kv-ve__ai-commander-actions">
                <button
                  type="button"
                  class="btn btn-ai kv-ve__ai-btn"
                  :disabled="aiLoading || !String(aiPrompt || '').trim()"
                  :aria-busy="aiLoading ? 'true' : 'false'"
                  @click="submitAiEdit"
                >
                  <span>{{ aiLoading ? $_("visualEditor-emailEditor-ai-applying") : $_("visualEditor-emailEditor-ai-apply") }}</span>
                </button>
              </div>
            </div>
          </div>

          <draggable
            class="kv-ve__palette"
            :list="blockPalette"
            :sort="false"
            :group="{ name: 'emailBlocks', pull: 'clone', put: false }"
            :clone="clonePaletteBlock"
            @start="onAnyDragStart('blocks', $event)"
            @end="onAnyDragEnd"
          >
            <div
              class="kv-ve__palette-item kv-ve__palette-item--tile"
              v-for="item in blockPalette"
              :key="item.type"
            >
              <div class="kv-ve__palette-tile-icon" :class="`kv-ve__palette-tile-icon--${item.type}`" aria-hidden="true">
                <span v-if="item.type === 'text'">T</span>
                <span v-else-if="item.type === 'image'">▦</span>
                <span v-else-if="item.type === 'button'">▭</span>
                <span v-else-if="item.type === 'divider'">—</span>
                <span v-else-if="item.type === 'spacer'">↕</span>
                <span v-else>•</span>
              </div>
              <div class="kv-ve__palette-label kv-ve__palette-label--center">{{ $_(item.labelKey) }}</div>
            </div>
          </draggable>
        </div>

        <!-- Modules Tab -->
        <div v-show="addElementsTab === 'modules'" class="kv-ve__group">
          <div v-if="modulesLoading" class="kv-ve__modules-loading">
            {{ $_("visualEditor-emailEditor-modules-loading") }}
          </div>
          <div v-else-if="!kveditorModules.length" class="kv-ve__modules-empty">
            {{ $_("visualEditor-emailEditor-modules-empty") }}
          </div>
          <draggable
            v-else
            class="kv-ve__palette kv-ve__palette--modules"
            :list="kveditorModules"
            :sort="false"
            :group="{ name: 'emailRows', pull: 'clone', put: false }"
            :clone="cloneModule"
            @start="onAnyDragStart('rows', $event)"
            @end="onAnyDragEnd"
          >
            <div
              class="kv-ve__palette-item kv-ve__palette-item--wide kv-ve__palette-item--module"
              v-for="module in kveditorModules"
              :key="module.id"
            >
              <div class="kv-ve__module-preview" aria-hidden="true">
                <button
                  v-if="canEditSavedAsset('module', module)"
                  type="button"
                  class="kv-ve__module-edit-btn"
                  :title="$_('visualEditor-emailEditor-tooltips-edit')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-edit')"
                  @click.stop="openEditSavedAssetModal('module', module)"
                  @mousedown.stop
                >
                  <span class="mdi mdi-pencil" aria-hidden="true"></span>
                </button>
                <img
                  v-if="module.screenshot"
                  :src="module.screenshot"
                  :alt="module.name"
                  loading="lazy"
                  class="kv-ve__module-preview-image"
                />
                <div v-else class="kv-ve__module-preview-fallback">{{ $_("visualEditor-emailEditor-templateGallery-noScreenshot") }}</div>
              </div>
            </div>
          </draggable>
        </div>

        <!-- Layout Tab -->
        <div v-show="addElementsTab === 'layout'" class="kv-ve__group">
          <div class="kv-ve__layout-actions">
            <KvButton variant="outlined" size="small" @click="addContainer">
              {{ $_("visualEditor-emailEditor-container-add") }}
            </KvButton>
          </div>
          <draggable
            class="kv-ve__palette kv-ve__palette--rows"
            :list="visibleRowPalette"
            :sort="false"
            :group="{ name: 'emailRows', pull: 'clone', put: false }"
            :clone="cloneRowTemplate"
            @start="onAnyDragStart('rows', $event)"
            @end="onAnyDragEnd"
          >
            <div
              class="kv-ve__palette-item kv-ve__palette-item--row"
              v-for="item in visibleRowPalette"
              :key="item.key"
            >
              <div class="kv-ve__palette-row-preview kv-ve__palette-row-preview--wide" :style="rowPalettePreviewStyle(item)" aria-hidden="true">
                <div v-for="i in item.columns" :key="i" class="kv-ve__palette-row-col"></div>
              </div>
              <div class="kv-ve__palette-row-caption">{{ item.caption }}</div>
            </div>
          </draggable>
        </div>
      </div>
    </div>

    <!-- Center: Email Editor -->
    <div class="kv-ve__main" @mouseleave="clearHoveredRow(); clearHoveredContainer()">
      <div class="kv-ve__topbar">
        <div class="kv-ve__topbar-left">
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-templateGallery')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :aria-label="$_('visualEditor-emailEditor-topbar-templateGallery')"
              @click="templateGalleryOpen = true"
            >
              <span class="mdi mdi-view-grid-outline" aria-hidden="true"></span>
            </button>
          </KvTooltip>
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-saveAsTemplate')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :aria-label="$_('visualEditor-emailEditor-topbar-saveAsTemplate')"
              @click="openSaveAssetModal('template')"
            >
              <span class="mdi mdi-content-save-outline" aria-hidden="true"></span>
            </button>
          </KvTooltip>
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-undo')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :disabled="!canUndo"
              :aria-label="$_('visualEditor-emailEditor-topbar-undo')"
              @click="undo"
            >
              <span class="mdi mdi-undo" aria-hidden="true"></span>
            </button>
          </KvTooltip>
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-redo')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :disabled="!canRedo"
              :aria-label="$_('visualEditor-emailEditor-topbar-redo')"
              @click="redo"
            >
              <span class="mdi mdi-redo" aria-hidden="true"></span>
            </button>
          </KvTooltip>
        </div>
        <div class="kv-ve__topbar-center">
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-preview')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :class="{ active: preview }"
              :aria-label="$_('visualEditor-emailEditor-topbar-preview')"
              @click="preview = true"
            >
              <Icon icon-class="ui-eye" size="4" />
            </button>
          </KvTooltip>
        </div>
        <div class="kv-ve__topbar-right">
          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-close')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :aria-label="$_('visualEditor-emailEditor-topbar-close')"
              @click="$emit('close')"
            >
              <span class="mdi mdi-close" aria-hidden="true"></span>
            </button>
          </KvTooltip>

          <KvButton class="kv-ve__save-btn" variant="filled" color="primary" size="small" @click="save">
            {{ $_("visualEditor-emailEditor-topbar-save") }}
          </KvButton>

          <KvTooltip :title="$_('visualEditor-emailEditor-topbar-settings')" placement="bottom">
            <button
              type="button"
              class="kv-ve__topbar-icon-btn"
              :class="{ active: emailDesignOpen }"
              :aria-label="$_('visualEditor-emailEditor-topbar-emailSettings')"
              @click="toggleEmailDesignOpen"
            >
              <Icon icon-class="ui-settings-02" size="4" />
            </button>
          </KvTooltip>
        </div>
      </div>

      <!-- AI loading overlay (blocks interactions while AI is generating) -->
      <transition name="kv-ve-fade">
        <div v-if="aiLoading" class="kv-ve__ai-loading-overlay" role="status" aria-live="polite" :aria-label="$_('visualEditor-emailEditor-ai-loadingAria')">
          <div class="kv-ve__ai-loading-card">
            <div class="kv-ve__ai-loading-spinner" aria-hidden="true"></div>
            <div class="kv-ve__ai-loading-title">{{ $_("visualEditor-emailEditor-ai-badge") }}</div>
            <div class="kv-ve__ai-loading-sentence">{{ aiLoadingSentence }}</div>
            <div class="kv-ve__ai-loading-subtitle">{{ $_("visualEditor-emailEditor-ai-loadingSubtitle") }}</div>
          </div>
        </div>
      </transition>

      <div
        class="kv-ve__canvas"
        :style="canvasStyle"
        @click.self="openGlobalSettings"
      >
        <div
          class="kv-ve__email"
          :style="{
            width: '100%',
            '--kv-ve-link-color': doc.body.linkColor,
            '--kv-ve-font-family': doc.body.fontFamily,
            '--kv-ve-text-color': doc.body.textColor,
            '--kv-ve-button-background-color': doc.body.buttonBackgroundColor,
            '--kv-ve-button-text-color': doc.body.buttonTextColor,
            '--kv-ve-font-size': '16px',
            '--kv-ve-line-height': '1.5',
          }"
        >
          <draggable
            v-model="doc.containers"
            :group="{ name: 'emailContainers', pull: true, put: ['emailContainers'] }"
            handle=".kv-ve__container-drag"
            class="kv-ve__containers"
            :options="{ animation: 150 }"
            @start="onAnyDragStart('containers', $event)"
            @end="onAnyDragEnd"
          >
            <div
              v-for="(container, cIdx) in doc.containers"
              :key="container.id"
              class="kv-ve__container"
              :class="{
                selected: focusedContainerId === container.id,
                'kv-ve__style-target': isStylePasteTargetContainer(container),
              }"
              :style="containerOuterStyle(container)"
              @click.stop="selectContainer(container)"
            >
              <button
                v-if="isMobileUi && mobilePending && (mobilePending.kind === 'row' || mobilePending.kind === 'module') && !isDragging"
                type="button"
                class="kv-ve__mobile-drop kv-ve__mobile-drop--row"
                :aria-label="$_('visualEditor-emailEditor-mobile-insertHere')"
                @click.stop="mobileApplyRow(container)"
              >
                <span class="kv-ve__mobile-drop-inner">
                  <span class="mdi mdi-plus" aria-hidden="true"></span>
                </span>
              </button>
              <button
                v-if="isMobileUi && mobileMovePending && mobileMovePending.kind === 'row' && !isDragging"
                type="button"
                class="kv-ve__mobile-drop kv-ve__mobile-drop--move kv-ve__mobile-drop--row"
                :aria-label="$_('visualEditor-emailEditor-mobile-moveRowHere')"
                @click.stop="mobileMoveRowTo(container)"
              >
                <span class="kv-ve__mobile-drop-inner">
                  <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                </span>
              </button>
              <button
                v-if="isMobileUi && mobileMovePending && mobileMovePending.kind === 'section' && !isDragging && mobileMovePending.containerId !== container.id"
                type="button"
                class="kv-ve__mobile-drop kv-ve__mobile-drop--move kv-ve__mobile-drop--row"
                :aria-label="$_('visualEditor-emailEditor-mobile-moveSectionHere')"
                @click.stop="mobileMoveSectionTo(container)"
              >
                <span class="kv-ve__mobile-drop-inner">
                  <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                </span>
              </button>
              <div
                v-if="focusedContainerId === container.id"
                class="kv-ve__container-controls"
                :ref="'containerControls-' + container.id"
                :style="getContainerControlsStyle(container)"
                :aria-label="$_('visualEditor-emailEditor-container-controls')"
              >
                <button
                  v-if="isMobileUi"
                  type="button"
                  class="kv-ve__container-tool kv-ve__container-tool--settings"
                  :title="$_('visualEditor-emailEditor-tooltips-settings')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-settings')"
                  @click.stop="onMobileSettingsIcon('section', container.id, 'section')"
                >
                  <Icon icon-class="ui-settings-02" size="4" />
                </button>
                <span v-else class="kv-ve__container-drag" :title="$_('visualEditor-emailEditor-tooltips-drag')">⋮⋮</span>
                <button
                  v-if="!isMobileUi || isMobileToolsExpanded('section', container.id)"
                  type="button"
                  class="kv-ve__container-tool"
                  :class="{ active: isStyleClipboardSourceContainer(container) }"
                  :title="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                  @click.stop="onBrushClickContainer(container)"
                >
                  <span class="mdi mdi-brush" aria-hidden="true"></span>
                </button>
                <button
                  v-if="!isMobileUi || isMobileToolsExpanded('section', container.id)"
                  type="button"
                  class="kv-ve__container-tool"
                  :title="$_('visualEditor-emailEditor-tooltips-duplicate')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-duplicate')"
                  @click.stop="duplicateContainer(container)"
                >
                  <span class="mdi mdi-content-copy" aria-hidden="true"></span>
                </button>
                <button
                  v-if="isMobileUi && isMobileToolsExpanded('section', container.id)"
                  type="button"
                  class="kv-ve__container-tool"
                  :title="$_('visualEditor-emailEditor-tooltips-move')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-move')"
                  @click.stop="startMobileMoveSection(container)"
                >
                  <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                </button>
                <button
                  v-if="!isMobileUi || isMobileToolsExpanded('section', container.id)"
                  type="button"
                  class="kv-ve__container-tool kv-ve__container-tool--danger"
                  :title="$_('visualEditor-emailEditor-tooltips-delete')"
                  :aria-label="$_('visualEditor-emailEditor-tooltips-delete')"
                  @click.stop="deleteContainer(container)"
                >
                  <span class="mdi mdi-close" aria-hidden="true"></span>
                </button>
              </div>

              <!-- Floating insert buttons (only for selected container) -->
              <div
                v-if="focusedContainerId === container.id && !isDragging"
                class="kv-ve__container-insert"
                aria-hidden="true"
              >
                <KvTooltip
                  class="kv-ve__container-insert-tip kv-ve__container-insert-tip--top"
                  placement="right"
                  :title="$_('visualEditor-emailEditor-tooltips-addSectionAbove')"
                  :style="cIdx === 0 ? { top: '0px' } : null"
                >
                  <button
                    type="button"
                    class="kv-ve__container-insert-btn"
                    :aria-label="$_('visualEditor-emailEditor-tooltips-addSectionAbove')"
                    @click.stop="insertContainerBefore(container)"
                  >
                    +
                  </button>
                </KvTooltip>

                <KvTooltip
                  class="kv-ve__container-insert-tip kv-ve__container-insert-tip--bottom"
                  placement="right"
                  :title="$_('visualEditor-emailEditor-tooltips-addSectionBelow')"
                  :style="cIdx === doc.containers.length - 1 ? { bottom: '0px' } : null"
                >
                  <button
                    type="button"
                    class="kv-ve__container-insert-btn"
                    :aria-label="$_('visualEditor-emailEditor-tooltips-addSectionBelow')"
                    @click.stop="insertContainerAfter(container)"
                  >
                    +
                  </button>
                </KvTooltip>
              </div>

              <div class="kv-ve__container-pad" :style="containerPadStyle(container)">
                <div class="kv-ve__container-content" :style="containerContentStyle(container)">
                <draggable
                  v-model="container.rows"
                  :group="{ name: 'emailRows', pull: true, put: ['emailRows'] }"
                  handle=".kv-ve__row-drag"
                  class="kv-ve__rows"
                  ref="rowsDraggable"
                  :ghost-class="'kv-ve-ghost'"
                  :drag-class="'kv-ve-drag'"
                  :chosen-class="'kv-ve__row-chosen'"
                  :options="{ animation: 150, dragClass: 'kv-ve__row-drag-box', ghostClass: 'kv-ve__row-ghost' }"
                  @start="onAnyDragStart('rows', $event)"
                  @end="onAnyDragEnd"
                  @change="onRowsChanged(container, $event)"
                >
                  <div
                    v-for="row in container.rows"
                    :key="row.id"
                    class="kv-ve__row"
                    :data-kv-ve-row-id="row.id"
                    :class="{
                      selected: focusedRowId === row.id,
                      'kv-ve__row--clone': row.__isDragClone,
                      'kv-ve__style-target': isStylePasteTargetRow(row),
                    }"
                    @click.stop="selectRow(row, container)"
                  >
                    <!-- Row handle. On hover it expands into a toolbar (drag + duplicate + remove). -->
                    <div
                      v-if="focusedRowId === row.id"
                      class="kv-ve__row-controls"
                      :ref="'rowControls-' + row.id"
                      :style="getRowControlsStyle(row)"
                      :aria-label="$_('visualEditor-emailEditor-tooltips-dragRow')"
                    >
                      <button
                        v-if="isMobileUi"
                        type="button"
                        class="kv-ve__row-tool kv-ve__row-tool--settings"
                        :title="$_('visualEditor-emailEditor-tooltips-settings')"
                        :aria-label="$_('visualEditor-emailEditor-tooltips-settings')"
                        @click.stop="onMobileSettingsIcon('row', row.id, 'row')"
                      >
                        <Icon icon-class="ui-settings-02" size="4" />
                      </button>
                      <div v-if="!isMobileUi" class="kv-ve__row-drag" :title="$_('visualEditor-emailEditor-tooltips-dragRow')">⋮⋮</div>
                      <button
                        type="button"
                        class="kv-ve__row-tool"
                        :class="{ active: isStyleClipboardSourceRow(row) }"
                        :title="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                        :aria-label="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                        @click.stop="onBrushClickRow(row)"
                        v-if="!isMobileUi || isMobileToolsExpanded('row', row.id)"
                      >
                        <span class="mdi mdi-brush" aria-hidden="true"></span>
                      </button>
                      <button
                        type="button"
                        class="kv-ve__row-tool"
                        :title="$_('visualEditor-emailEditor-tooltips-duplicate')"
                        :aria-label="$_('visualEditor-emailEditor-tooltips-duplicate')"
                        @click.stop="duplicateRow(row)"
                        v-if="!isMobileUi || isMobileToolsExpanded('row', row.id)"
                      >
                        <span class="mdi mdi-content-copy" aria-hidden="true"></span>
                      </button>
                      <button
                        v-if="isMobileUi && isMobileToolsExpanded('row', row.id)"
                        type="button"
                        class="kv-ve__row-tool"
                        :title="$_('visualEditor-emailEditor-tooltips-move')"
                        :aria-label="$_('visualEditor-emailEditor-tooltips-move')"
                        @click.stop="startMobileMoveRow(container, row)"
                      >
                        <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                      </button>
                      <button
                        type="button"
                        class="kv-ve__row-tool"
                        :title="$_('visualEditor-emailEditor-tooltips-saveAsModule')"
                        :aria-label="$_('visualEditor-emailEditor-tooltips-saveAsModule')"
                        @click.stop="openSaveAssetModal('module', { row })"
                        v-show="!isMobileUi || isMobileToolsExpanded('row', row.id)"
                      >
                        <span class="mdi mdi-content-save-outline" aria-hidden="true"></span>
                      </button>
                      <button
                        type="button"
                        class="kv-ve__row-tool kv-ve__row-tool--danger"
                        :title="$_('visualEditor-emailEditor-actions-deleteRow')"
                        :aria-label="$_('visualEditor-emailEditor-actions-deleteRow')"
                        @click.stop="deleteRow(row)"
                        v-if="!isMobileUi || isMobileToolsExpanded('row', row.id)"
                      >
                        <span class="mdi mdi-close" aria-hidden="true"></span>
                      </button>
                    </div>
                    <div class="kv-ve__row-margin" :style="rowMarginStyle(row)">
                    <div class="kv-ve__row-inner" :style="rowStyle(row)">
                      <table class="kv-ve__row-table" role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                        <tr>
                          <template v-for="(item, itemIdx) in getColumnsWithGaps(row)">
                            <!-- Gap column -->
                            <td
                              v-if="item.type === 'gap'"
                              :key="`gap-${itemIdx}`"
                              :style="gapColumnStyle(item.width)"
                              class="kv-ve__gap-col"
                            ></td>
                            <!-- Content column -->
                            <td
                              v-else
                              :key="item.col.id"
                              :valign="columnValign(item.col)"
                              :width="columnWidthPct(row, item.idx) + '%'"
                              :style="columnTdStyle(row, item.idx, row.columns.length, item.col)"
                              @click.stop="selectCol(row, item.col)"
                              :class="{
                                'kv-ve__col-td': true,
                                'kv-ve__col-td--selected': focusedColId === item.col.id,
                                'kv-ve__style-target': isStylePasteTargetCol(item.col),
                              }"
                            >
                              <button
                                v-if="isMobileUi && mobileMovePending && mobileMovePending.kind === 'col' && mobileMovePending.rowId === row.id && !isDragging"
                                type="button"
                                class="kv-ve__mobile-drop kv-ve__mobile-drop--move kv-ve__mobile-drop--col"
                                :aria-label="$_('visualEditor-emailEditor-mobile-moveColumnHere')"
                                @click.stop="mobileMoveColTo(row, item.col)"
                              >
                                <span class="kv-ve__mobile-drop-inner">
                                  <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                                </span>
                              </button>
                              <div
                                v-if="focusedColId === item.col.id && !isDragging"
                                class="kv-ve__col-controls"
                                :ref="'colControls-' + item.col.id"
                                :style="getColControlsStyle(item.col)"
                                :aria-label="$_('visualEditor-emailEditor-column-controls')"
                                @click.stop
                              >
                                <button
                                  v-if="isMobileUi"
                                  type="button"
                                  class="kv-ve__col-tool kv-ve__col-tool--settings"
                                  :title="$_('visualEditor-emailEditor-tooltips-settings')"
                                  :aria-label="$_('visualEditor-emailEditor-tooltips-settings')"
                                  @click.stop="onMobileSettingsIcon('col', item.col.id, 'column')"
                                >
                                  <Icon icon-class="ui-settings-02" size="4" />
                                </button>
                                <button
                                  v-if="!isMobileUi || isMobileToolsExpanded('col', item.col.id)"
                                  type="button"
                                  class="kv-ve__col-tool"
                                  :class="{ active: styleClipboard?.kind === 'column' && isStyleClipboardSourceCol(item.col) }"
                                  :title="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                                  :aria-label="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                                  @click.stop="onBrushClickCol(item.col)"
                                >
                                  <span class="mdi mdi-brush" aria-hidden="true"></span>
                                </button>
                                <button
                                  v-if="!isMobileUi || isMobileToolsExpanded('col', item.col.id)"
                                  type="button"
                                  class="kv-ve__col-tool"
                                  :class="{ active: styleClipboard?.kind === 'column-duplicate' && isStyleClipboardSourceCol(item.col) }"
                                  :title="$_('visualEditor-emailEditor-actions-duplicateColumn')"
                                  :aria-label="$_('visualEditor-emailEditor-actions-duplicateColumn')"
                                  @click.stop="onDuplicateClickCol(item.col)"
                                >
                                  <span class="mdi mdi-content-copy" aria-hidden="true"></span>
                                </button>
                                <button
                                  v-if="isMobileUi && isMobileToolsExpanded('col', item.col.id)"
                                  type="button"
                                  class="kv-ve__col-tool"
                                  :title="$_('visualEditor-emailEditor-tooltips-move')"
                                  :aria-label="$_('visualEditor-emailEditor-tooltips-move')"
                                  @click.stop="startMobileMoveCol(row, item.col)"
                                >
                                  <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                                </button>
                              </div>
                              <div
                                class="kv-ve__col"
                                :style="columnInnerStyle(item.col)"
                                @click.stop="selectCol(row, item.col)"
                              >
                                <button
                                  v-if="isMobileUi && mobilePending && mobilePending.kind === 'block'"
                                  type="button"
                                  class="kv-ve__mobile-drop kv-ve__mobile-drop--block"
                                  :aria-label="$_('visualEditor-emailEditor-mobile-insertHere')"
                                  @click.stop="mobileApplyBlock(row, item.col)"
                                >
                                  <span class="kv-ve__mobile-drop-inner">
                                    <span class="mdi mdi-plus" aria-hidden="true"></span>
                                  </span>
                                </button>
                                <button
                                  v-if="isMobileUi && mobileMovePending && mobileMovePending.kind === 'block' && !isDragging"
                                  type="button"
                                  class="kv-ve__mobile-drop kv-ve__mobile-drop--move kv-ve__mobile-drop--block"
                                  :aria-label="$_('visualEditor-emailEditor-mobile-moveBlockHere')"
                                  @click.stop="mobileMoveBlockTo(row, item.col)"
                                >
                                  <span class="kv-ve__mobile-drop-inner">
                                    <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                                  </span>
                                </button>
                                <draggable
                                  v-model="item.col.blocks"
                                  :group="{ name: 'emailBlocks', pull: true, put: ['emailBlocks'] }"
                                  class="kv-ve__col-drag"
                                  :ghost-class="'kv-ve-ghost'"
                                  :options="{ animation: 150, handle: '.kv-ve__drag-handle' }"
                                  @start="onAnyDragStart('blocks', $event)"
                                  @end="onAnyDragEnd"
                                >
                                <div
                                  v-for="block in item.col.blocks"
                                  :key="block.id"
                                  class="kv-ve__block-wrap"
                                  :class="{
                                        'kv-ve__block-wrap--inline':
                                          (block?.type === 'image' || block?.type === 'text') &&
                                          (String(block?.settings?.display || '').toLowerCase() === 'inline-block' ||
                                            String(block?.settings?.display || '').toLowerCase() === 'inline'),
                                  }"
                                  :style="blockMarginStyle(block)"
                                  @mousedown.capture="selectedBlockId !== block.id && selectBlock(row, item.col, block)"
                                  @focusin.capture="selectedBlockId !== block.id && selectBlock(row, item.col, block)"
                                  @click.stop="selectedBlockId !== block.id && selectBlock(row, item.col, block)"
                                  @touchend="onBlockTouchEnd(row, item.col, block, $event)"
                                  @dblclick.stop="onBlockDblClick(row, item.col, block)"
                                >
                                  <div
                                    class="kv-ve__block"
                                    :class="{
                                      selected: focusedBlockId === block.id,
                                      ['kv-ve__block--' + block.type]: true,
                                      'kv-ve__style-target': isStylePasteTargetBlock(block),
                                    }"
                                  >
                                    <div
                                      v-if="selectedBlockId === block.id && !isDragging"
                                      class="kv-ve__block-tools"
                                      :ref="'blockControls-' + block.id"
                                      :style="getBlockControlsStyle(block)"
                                    >
                                      <button
                                        v-if="isMobileUi"
                                        type="button"
                                        class="kv-ve__tool kv-ve__tool--settings"
                                        :title="$_('visualEditor-emailEditor-tooltips-settings')"
                                        :aria-label="$_('visualEditor-emailEditor-tooltips-settings')"
                                        @click.stop="onMobileSettingsIcon('block', block.id, 'block')"
                                      >
                                        <Icon icon-class="ui-settings-02" size="4" />
                                      </button>
                                      <span v-if="!isMobileUi" class="kv-ve__drag-handle" :title="$_('visualEditor-emailEditor-tooltips-dragBlock')">⋮⋮</span>
                                      <button
                                        v-if="!isMobileUi || isMobileToolsExpanded('block', block.id)"
                                        class="kv-ve__tool"
                                        type="button"
                                        :class="{ active: isStyleClipboardSourceBlock(block) }"
                                        :title="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                                        :aria-label="$_('visualEditor-emailEditor-tooltips-copyStyles')"
                                        @click.stop="onBrushClickBlock(block)"
                                      >
                                        <span class="mdi mdi-brush" aria-hidden="true"></span>
                                      </button>
                                      <button
                                        v-if="!isMobileUi || isMobileToolsExpanded('block', block.id)"
                                        type="button"
                                        class="kv-ve__tool"
                                        :title="$_('visualEditor-emailEditor-tooltips-duplicate')"
                                        @click.stop="duplicateBlock(row, item.col, block)">
                                        ⧉
                                      </button>
                                      <button
                                        v-if="isMobileUi && isMobileToolsExpanded('block', block.id)"
                                        type="button"
                                        class="kv-ve__tool"
                                        :title="$_('visualEditor-emailEditor-tooltips-move')"
                                        :aria-label="$_('visualEditor-emailEditor-tooltips-move')"
                                        @click.stop="startMobileMoveBlock(row, item.col, block)"
                                      >
                                        <span class="mdi mdi-cursor-move" aria-hidden="true"></span>
                                      </button>
                                      <button
                                        v-if="!isMobileUi || isMobileToolsExpanded('block', block.id)"
                                        type="button"
                                        class="kv-ve__tool danger"
                                        :title="$_('visualEditor-emailEditor-tooltips-delete')"
                                        @click.stop="deleteBlock(row, item.col, block)">
                                        <span class="mdi mdi-close" aria-hidden="true"></span>
                                      </button>
                                    </div>

                                    <div class="kv-ve__block-content">
                                      <component
                                        :is="blockComponent(block.type)"
                                        :block="block"
                                        :selected="selectedBlockId === block.id"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div v-if="!item.col.blocks.length" class="kv-ve__drop-empty">
                                  {{ $_("visualEditor-emailEditor-emptyState-dragBlocksHere") }}
                                </div>
                              </draggable>
                            </div>
                          </td>
                          </template>
                        </tr>
                      </table>
                    </div>
                    </div>
                  </div>

                  <div v-if="!container.rows.length" class="kv-ve__drop-empty">
                    {{ $_("visualEditor-emailEditor-emptyState-dragModulesOrLayoutsHere") }}
                  </div>
                </draggable>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </div>

      <MobileInsertBar
        v-if="isMobileUi && !preview"
        :open.sync="mobileBarOpen"
        :mode.sync="mobileInsertMode"
        :pending="mobileAnyPending"
        :blocks="mobileBlocks"
        :rows="rowPalette"
        :modules="kveditorModules"
        :modulesLoading="modulesLoading"
        @select="onMobileSelect"
        @cancel="cancelMobilePending"
      />

      <!-- Mobile action menu (opened by cog) -->
      <div
        v-if="isMobileUi && mobileActionMenu"
        class="kv-ve__mobile-action-overlay"
        role="presentation"
        @click.self="closeMobileActionMenu"
      >
        <div
          class="kv-ve__mobile-action-sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="$_('visualEditor-emailEditor-mobileActionMenu-title')"
        >
          <div class="kv-ve__mobile-action-header">
            <div class="kv-ve__mobile-action-title">{{ $_("visualEditor-emailEditor-mobileActionMenu-title") }}</div>
            <button
              type="button"
              class="kv-ve__mobile-action-close"
              :aria-label="$_('visualEditor-emailEditor-topbar-close')"
              @click="closeMobileActionMenu"
            >
              <span class="mdi mdi-close" aria-hidden="true"></span>
            </button>
          </div>
          <div class="kv-ve__mobile-action-body">
            <button type="button" class="kv-ve__mobile-action-btn" @click="mobileActionEditSettings">
              <span class="kv-ve__mobile-action-ic"><Icon icon-class="ui-settings-02" size="4" /></span>
              <span>{{ $_("visualEditor-emailEditor-mobileActionMenu-editSettings") }}</span>
            </button>
            <button type="button" class="kv-ve__mobile-action-btn" @click="mobileActionMove">
              <span class="kv-ve__mobile-action-ic"><span class="mdi mdi-cursor-move" aria-hidden="true"></span></span>
              <span>{{ $_("visualEditor-emailEditor-tooltips-move") }}</span>
            </button>
            <button v-if="mobileActionCanDuplicate" type="button" class="kv-ve__mobile-action-btn" @click="mobileActionDuplicate">
              <span class="kv-ve__mobile-action-ic"><span class="mdi mdi-content-copy" aria-hidden="true"></span></span>
              <span>{{ $_("visualEditor-emailEditor-tooltips-duplicate") }}</span>
            </button>
            <button v-if="mobileActionCanCopyStyles" type="button" class="kv-ve__mobile-action-btn" @click="mobileActionCopyStyles">
              <span class="kv-ve__mobile-action-ic"><span class="mdi mdi-brush" aria-hidden="true"></span></span>
              <span>{{ $_("visualEditor-emailEditor-tooltips-copyStyles") }}</span>
            </button>
            <button v-if="mobileActionCanDelete" type="button" class="kv-ve__mobile-action-btn kv-ve__mobile-action-btn--danger" @click="mobileActionDelete">
              <span class="kv-ve__mobile-action-ic"><span class="mdi mdi-delete-outline" aria-hidden="true"></span></span>
              <span>{{ $_("visualEditor-emailEditor-tooltips-delete") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar: Settings -->
    <div
      class="kv-ve__sidebar kv-ve__sidebar--settings"
      :class="{
        'kv-ve__sidebar--settings-open': isMobileUi ? mobileSettingsOpen : isSettingsOverlayOpen,
        'kv-ve__sidebar--settings-mobile': isMobileUi,
      }"
      v-if="!isMobileUi || mobileSettingsOpen"
    >
      <div class="kv-ve__sidebar-scroll kv-scrollbar kv-ve__sidebar-scroll--settings">
        <div class="kv-ve__tabs kv-ve__tabs--settings">
          <button
            v-if="!isMobileUi"
            type="button"
            class="kv-ve__settings-back"
            :aria-label="$_('visualEditor-emailEditor-topbar-backToEditor')"
            :title="$_('visualEditor-emailEditor-topbar-backToEditor')"
            @click="closeSettingsOverlay"
          >
            <span class="mdi mdi-arrow-left" aria-hidden="true"></span>
          </button>
          <button
            v-if="isMobileUi"
            type="button"
            class="kv-ve__settings-close"
            :aria-label="$_('visualEditor-emailEditor-topbar-close')"
            :title="$_('visualEditor-emailEditor-topbar-close')"
            @click="closeMobileSettingsDrawer"
          >
            <span class="mdi mdi-close" aria-hidden="true"></span>
          </button>
          <KvTabs v-model="settingsTab" grow>
            <template #tabs>
              <KvTab value="block">{{ $_("visualEditor-emailEditor-settingsTabs-block") }}</KvTab>
              <KvTab value="column">{{ $_("visualEditor-emailEditor-settingsTabs-column") }}</KvTab>
              <KvTab value="row">{{ $_("visualEditor-emailEditor-settingsTabs-row") }}</KvTab>
              <KvTab value="section">{{ $_("visualEditor-emailEditor-settingsTabs-section") }}</KvTab>
            </template>
          </KvTabs>
        </div>

        <!-- Bloco -->
        <div v-show="settingsTab === 'block'">
          <VeTextBlockSettings
            v-if="selectedBlock && selectedBlock.type === 'text'"
            :block="selectedBlock"
            :paletteLabel="paletteLabel(selectedBlock.type)"
            @delete="deleteSelectedBlock"
          />
          <VeImageBlockSettings
            v-else-if="selectedBlock && selectedBlock.type === 'image'"
            :block="selectedBlock"
            :paletteLabel="paletteLabel(selectedBlock.type)"
            :borderRadiusOptions="borderRadiusOptions"
            @delete="deleteSelectedBlock"
          />
          <VeButtonBlockSettings
            v-else-if="selectedBlock && selectedBlock.type === 'button'"
            :block="selectedBlock"
            :paletteLabel="paletteLabel(selectedBlock.type)"
            :borderRadiusOptions="borderRadiusOptions"
            @delete="deleteSelectedBlock"
          />
          <VeDividerBlockSettings
            v-else-if="selectedBlock && selectedBlock.type === 'divider'"
            :block="selectedBlock"
            :paletteLabel="paletteLabel(selectedBlock.type)"
            @delete="deleteSelectedBlock"
          />
          <VeSpacerBlockSettings
            v-else-if="selectedBlock && selectedBlock.type === 'spacer'"
            :block="selectedBlock"
            :paletteLabel="paletteLabel(selectedBlock.type)"
            @delete="deleteSelectedBlock"
          />
          <div v-else class="kv-ve__settings-empty">
            {{ $_("visualEditor-emailEditor-settingsEmpty-block") }}
          </div>
        </div>

        <!-- Coluna -->
        <div v-show="settingsTab === 'column'">
          <VeColumnSettings
            v-if="selectedCol"
            :col="selectedCol"
            :borderRadiusOptions="borderRadiusOptions"
          />
          <div v-else class="kv-ve__settings-empty">
            {{ $_("visualEditor-emailEditor-settingsEmpty-column") }}
          </div>
        </div>

        <!-- Linha -->
        <div v-show="settingsTab === 'row'">
          <VeRowSettings
            v-if="selectedRow"
            :row="selectedRow"
            :rowPalette="rowPalette"
            :borderRadiusOptions="borderRadiusOptions"
            @delete="deleteSelectedRow"
          />
          <div v-else class="kv-ve__settings-empty">
            {{ $_("visualEditor-emailEditor-settingsEmpty-row") }}
          </div>
        </div>

        <!-- Seção (Container) -->
        <div v-show="settingsTab === 'section'">
          <VeContainerSettings
            v-if="selectedContainer"
            :container="selectedContainer"
            :doc="doc"
            :borderRadiusOptions="borderRadiusOptions"
            @delete="deleteSelectedContainer"
          />
          <div v-else class="kv-ve__settings-empty">
            {{ $_("visualEditor-emailEditor-settingsEmpty-section") }}
          </div>
        </div>
      </div>
    </div>

    <!-- Email design settings overlay (opens from gear icon) -->
    <transition name="slide-right">
      <div v-if="emailDesignOpen" class="kv-ve__email-design-overlay" role="dialog" aria-modal="true" :aria-label="$_('visualEditor-emailEditor-topbar-emailSettings')">
      <div class="kv-ve__email-design-header">
        <div class="kv-ve__email-design-title">{{ $_("visualEditor-emailEditor-topbar-emailSettings") }}</div>
        <button
          type="button"
          class="kv-ve__topbar-icon-btn"
          :title="$_('visualEditor-emailEditor-topbar-close')"
          :aria-label="$_('visualEditor-emailEditor-topbar-close')"
          @click="emailDesignOpen = false"
        >
          <span class="mdi mdi-close" aria-hidden="true"></span>
        </button>
      </div>
      <div class="kv-ve__email-design-body kv-scrollbar">
        <VeEmailDesignSettings :doc="doc" :borderRadiusOptions="borderRadiusOptions" />
      </div>
      </div>
    </transition>

    <!-- Preview drawer (bottom-to-top, full screen) -->
    <transition name="slide-up">
      <div
        v-if="preview"
        class="kv-ve__preview-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="$_('visualEditor-emailEditor-topbar-preview')"
      >
      <div class="kv-ve__preview-drawer-topbar">
        <div class="kv-ve__preview-drawer-title">{{ $_("visualEditor-emailEditor-topbar-preview") }}</div>
        <div class="kv-ve__preview-drawer-actions">
          <div class="kv-ve__device-toggle" role="group" :aria-label="$_('visualEditor-emailEditor-preview-device')">
            <button
              type="button"
              class="kv-ve__device-btn"
              :class="{ active: previewDevice === 'desktop' }"
              :title="$_('visualEditor-emailEditor-preview-desktop')"
              :aria-label="$_('visualEditor-emailEditor-preview-desktop')"
              @click="previewDevice = 'desktop'"
            >
              🖥
            </button>
            <button
              type="button"
              class="kv-ve__device-btn"
              :class="{ active: previewDevice === 'mobile' }"
              :title="$_('visualEditor-emailEditor-preview-mobile')"
              :aria-label="$_('visualEditor-emailEditor-preview-mobile')"
              @click="previewDevice = 'mobile'"
            >
              📱
            </button>
          </div>
          <button
            type="button"
            class="kv-ve__topbar-icon-btn"
            :title="$_('visualEditor-emailEditor-topbar-close')"
            :aria-label="$_('visualEditor-emailEditor-topbar-close')"
            @click="preview = false"
          >
            <span class="mdi mdi-close" aria-hidden="true"></span>
          </button>
        </div>
      </div>
      <div class="kv-ve__preview-drawer-body">
        <div class="kv-ve__preview-viewport" :class="`kv-ve__preview-viewport--${previewDevice}`">
          <iframe
            class="kv-ve__iframe"
            :srcdoc="previewHtml"
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer"
            ref="previewFrame"
            @load="updatePreviewFrameHeight"
          ></iframe>
        </div>
      </div>
      </div>
    </transition>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import html2canvas from "html2canvas";

import VeTextBlock from "./blocks/VeTextBlock.vue";
import VeImageBlock from "./blocks/VeImageBlock.vue";
import VeButtonBlock from "./blocks/VeButtonBlock.vue";
import VeDividerBlock from "./blocks/VeDividerBlock.vue";
import VeSpacerBlock from "./blocks/VeSpacerBlock.vue";

import MobileInsertBar from "./MobileInsertBar.vue";

import KvTabs from "@/components/tabs/kv-tabs.vue";
import KvTab from "@/components/tabs/kv-tab.vue";
import KvTypography from "@/components/ui/kv-typography.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvModal from "@/components/modal/modal.vue";
import KvTooltip from "@/components/ui/kv-tooltip.vue";
import Icon from "@/components/icon/icon.vue";
import FilesGalleryModal from "@/files-gallery/files-gallery-modal.vue";
import keviApi from "@/keviApi";

import VeEmailDesignSettings from "./settings/VeEmailDesignSettings.vue";
import VeRowSettings from "./settings/VeRowSettings.vue";
import VeColumnSettings from "./settings/VeColumnSettings.vue";
import VeContainerSettings from "./settings/VeContainerSettings.vue";
import VeTextBlockSettings from "./settings/blocks/VeTextBlockSettings.vue";
import VeImageBlockSettings from "./settings/blocks/VeImageBlockSettings.vue";
import VeButtonBlockSettings from "./settings/blocks/VeButtonBlockSettings.vue";
import VeDividerBlockSettings from "./settings/blocks/VeDividerBlockSettings.vue";
import VeSpacerBlockSettings from "./settings/blocks/VeSpacerBlockSettings.vue";

import {
  createBlock,
  createBlankEmailDoc,
  createContainer,
  createDefaultEmailDoc,
  createRow,
} from "./lib/defaultEmailDoc";
import { EMAIL_SECTIONS } from "./lib/sections";
import { renderEmailHtml } from "./lib/renderEmailHtml";
import { kvVeId } from "./lib/id";

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export default {
  name: "EmailVisualEditor",
  components: {
    draggable,
    VeTextBlock,
    VeImageBlock,
    VeButtonBlock,
    VeDividerBlock,
    VeSpacerBlock,
    KvTabs,
    KvTab,
    KvTypography,
    KvButton,
    KvModal,
    KvTooltip,
    Icon,
    FilesGalleryModal,
    VeEmailDesignSettings,
    VeRowSettings,
    VeColumnSettings,
    VeContainerSettings,
    VeTextBlockSettings,
    VeImageBlockSettings,
    VeButtonBlockSettings,
    VeDividerBlockSettings,
    VeSpacerBlockSettings,
    MobileInsertBar,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    // Snippets/tokens used by automations (e.g. [[cliente.nome]]).
    // The automation screen can pass this list down so the editor can offer autocomplete.
    snippets: {
      type: Array,
      default: () => [],
    },
  },
  provide() {
    // Vue 2 provide/inject isn't reactive by default; use a getter function.
    return {
      kvVeGetSnippets: () => this.snippets,
    };
  },
  data() {
    return {
      doc: this.value ? deepClone(this.value) : createDefaultEmailDoc(),
      isMobileUi: false,
      mobileBarOpen: false,
      mobileInsertMode: "blocks", // blocks | layout | modules
      mobilePending: null, // { kind: 'block'|'row'|'module', ... }
      mobileMovePending: null, // { kind: 'block'|'col'|'row'|'section', ... }
      mobileSettingsOpen: false,
      mobileToolsExpandedKey: null, // e.g. "block:<id>", "row:<id>", "col:<id>", "section:<id>"
      mobileActionMenu: null, // { kind: 'block'|'col'|'row'|'section', tab: string, ...ctx }
      mobileLastTapTs: 0,
      mobileLastTapBlockId: null,
      preview: false,
      previewDevice: "desktop", // desktop | mobile
      isDragging: false,
      dragGroup: null, // null | 'blocks' | 'rows' | 'containers'
      templateGalleryOpen: false,
      templateGalleryLoading: false,
      kveditorTemplates: [],
      kveditorModules: [],
      modulesLoading: false,
      emailDesignOpen: false,
      // Save / edit template/module modal
      saveAssetModalOpen: false,
      saveAssetMode: "create", // create | edit
      saveAssetKind: "template", // template | module
      saveAssetName: "",
      saveAssetVisibility: "place", // place | global
      saveAssetSaving: false,
      saveAssetEditingItem: null, // item from API list
      saveAssetTargetRow: null, // row object (for saving modules)
      prevDocUserSelect: null,
      imageGalleryOpen: false,
      imageGalleryTarget: null,
      borderRadiusOptions: [
        { value: 0, labelKey: "visualEditor-emailEditor-common-borderRadiusSquare" },
        { value: 8, labelKey: "visualEditor-emailEditor-common-borderRadiusMedium" },
        { value: 16, labelKey: "visualEditor-emailEditor-common-borderRadiusLarge" },
        { value: 32, labelKey: "visualEditor-emailEditor-common-borderRadiusXLarge" },
        { value: 9999, labelKey: "visualEditor-emailEditor-common-borderRadiusCircle" },
      ],

      selectedRowId: null,
      selectedColId: null,
      selectedBlockId: null,
      selectedContainerId: null,
      hoveredRowId: null,
      hoveredContainerId: null,
      hoveredBlockId: null,
      settingsTab: "block", // block | column | row | section
      historyPast: [],
      historyFuture: [],
      historyTimer: null,
      isRestoringHistory: false,
      htmlUpdateTimer: null,
      showMoreRows: false,
      showMoreSections: false,
      addElementsTab: "blocks", // blocks | modules | layout
      blockPalette: [
        { type: "text", labelKey: "visualEditor-emailEditor-palette-blocks-text" },
        { type: "image", labelKey: "visualEditor-emailEditor-palette-blocks-image" },
        { type: "button", labelKey: "visualEditor-emailEditor-palette-blocks-button" },
        { type: "divider", labelKey: "visualEditor-emailEditor-palette-blocks-divider" },
        { type: "spacer", labelKey: "visualEditor-emailEditor-palette-blocks-spacer" },
      ],
      rowPalette: [
        { key: "row_1", kind: "custom", columns: 1, weights: [1], caption: "1" },
        { key: "row_2", kind: "custom", columns: 2, weights: [1, 1], caption: "2" },
        { key: "row_3", kind: "custom", columns: 3, weights: [1, 1, 1], caption: "3" },
        { key: "row_12", kind: "custom", columns: 2, weights: [1, 2], caption: "1:2" },
        { key: "row_21", kind: "custom", columns: 2, weights: [2, 1], caption: "2:1" },
        { key: "row_13", kind: "custom", columns: 2, weights: [1, 3], caption: "1:3" },
        { key: "row_31", kind: "custom", columns: 2, weights: [3, 1], caption: "3:1" },
        { key: "row_4", kind: "custom", columns: 4, weights: [1, 1, 1, 1], caption: "4" },
      ],
      sections: EMAIL_SECTIONS,

      // Style clipboard / paste mode
      styleClipboard: null,

      // AI prompt (edit current doc with natural language)
      aiPrompt: "",
      aiLoading: false,
      aiPromptHeight: 150,

      // AI loader sentences (like segments loader)
      aiLoadingSentenceIdx: 0,
      aiLoadingTimer: null,
      aiLoadingSentences: [
        this.$_("visualEditor-emailEditor-ai-loadingSentenceAnalyze"),
        this.$_("visualEditor-emailEditor-ai-loadingSentenceStructure"),
        this.$_("visualEditor-emailEditor-ai-loadingSentenceApply"),
        this.$_("visualEditor-emailEditor-ai-loadingSentenceAdjust"),
        this.$_("visualEditor-emailEditor-ai-loadingSentenceReview"),
      ],
    };
  },
  computed: {
    isGlobalAdmin() {
      return Boolean(this.$store?.state?.user?.global_admin);
    },
    currentPlaceId() {
      return this.$store?.state?.place?.id || null;
    },
    saveAssetKindLabel() {
      return this.saveAssetKind === "module"
        ? this.$_("visualEditor-emailEditor-saveAsset-kindModule")
        : this.$_("visualEditor-emailEditor-saveAsset-kindTemplate");
    },
    styleClipboardActive() {
      return Boolean(this.styleClipboard && this.styleClipboard.kind);
    },
    isSettingsOverlayOpen() {
      return Boolean(
        this.selectedBlockId ||
          this.selectedColId ||
          this.selectedRowId ||
          this.selectedContainerId ||
          this.emailDesignOpen,
      );
    },
    visibleSections() {
      return this.sections;
    },
    visibleRowPalette() {
      return this.rowPalette;
    },
    templateGalleryItems() {
      const blank = {
        __blank: true,
        id: "kveditor:blank",
        name: this.$_("visualEditor-emailEditor-templateGallery-startBlank"),
        places_id: null,
        screenshot: null,
      };
      const list = Array.isArray(this.kveditorTemplates) ? this.kveditorTemplates : [];
      return [blank, ...list];
    },
    mobileBlocks() {
      return [
        { type: "text", title: this.$_("visualEditor-emailEditor-palette-blocks-text"), iconMdi: "mdi-format-text", fallback: "T" },
        { type: "image", title: this.$_("visualEditor-emailEditor-palette-blocks-image"), iconMdi: "mdi-image-outline", fallback: "▦" },
        { type: "button", title: this.$_("visualEditor-emailEditor-palette-blocks-button"), iconMdi: "mdi-gesture-tap-button", fallback: "▭" },
        { type: "divider", title: this.$_("visualEditor-emailEditor-palette-blocks-divider"), iconMdi: "mdi-minus", fallback: "—" },
        { type: "spacer", title: this.$_("visualEditor-emailEditor-palette-blocks-spacer"), iconMdi: "mdi-arrow-expand-vertical", fallback: "↕" },
      ];
    },
    mobileAnyPending() {
      return this.mobilePending || this.mobileMovePending;
    },
    mobileActionCanDuplicate() {
      const k = this.mobileActionMenu?.kind;
      return k === "block" || k === "col" || k === "row" || k === "section";
    },
    mobileActionCanCopyStyles() {
      const k = this.mobileActionMenu?.kind;
      return k === "block" || k === "col" || k === "row" || k === "section";
    },
    mobileActionCanDelete() {
      const k = this.mobileActionMenu?.kind;
      return k === "block" || k === "row" || k === "section";
    },
    exportedHtml() {
      try {
        return renderEmailHtml(this.doc, { mode: "export" });
      } catch (e) {
        console.error(e);
        return `<pre>${this.$_("visualEditor-emailEditor-errors-renderHtmlFailed")}</pre>`;
      }
    },
    previewHtml() {
      try {
        return renderEmailHtml(this.doc, { mode: "preview" });
      } catch (e) {
        console.error(e);
        return `<pre>${this.$_("visualEditor-emailEditor-errors-renderHtmlFailed")}</pre>`;
      }
    },
    allRows() {
      const out = [];
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      containers.forEach((c) => {
        (c?.rows || []).forEach((r) => out.push(r));
      });
      return out;
    },
    selectedContainer() {
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      if (!containers.length) return null;

      if (this.selectedContainerId) {
        return containers.find((c) => c.id === this.selectedContainerId) || null;
      }

      // Fall back to container that holds the currently selected row/block.
      const row =
        this.selectedRow ||
        (() => {
          if (!this.selectedBlockId) return null;
          for (const r of this.allRows) {
            for (const c of r.columns || []) {
              if ((c.blocks || []).some((b) => b.id === this.selectedBlockId)) return r;
            }
          }
          return null;
        })();

      if (row?.id) return this.findContainerByRowId(row.id);
      return null;
    },
    activeContainerId() {
      return this.getActiveContainer()?.id || null;
    },
    focusedBlockId() {
      return this.settingsTab === "block" ? this.selectedBlockId : null;
    },
    focusedColId() {
      return this.settingsTab === "column" ? (this.selectedCol?.id || null) : null;
    },
    focusedRowId() {
      return this.settingsTab === "row" ? (this.selectedRow?.id || null) : null;
    },
    focusedContainerId() {
      return this.settingsTab === "section" ? (this.selectedContainer?.id || null) : null;
    },
    selectedRow() {
      if (this.selectedRowId) {
        return this.allRows.find((r) => r.id === this.selectedRowId) || null;
      }

      // If a block is selected, infer its row.
      if (this.selectedBlockId) {
        for (const r of this.allRows) {
          for (const c of r.columns || []) {
            if ((c.blocks || []).some((b) => b.id === this.selectedBlockId)) return r;
          }
        }
      }
      return null;
    },
    selectedCol() {
      // Prefer explicit selection.
      if (this.selectedColId) {
        const row = this.selectedRow;
        if (row) {
          const hit = (row.columns || []).find((c) => c.id === this.selectedColId);
          if (hit) return hit;
        }
        // Fallback: search all rows (handles edge cases when selectedRowId is stale).
        for (const r of this.allRows) {
          const hit = (r.columns || []).find((c) => c.id === this.selectedColId);
          if (hit) return hit;
        }
      }

      // If a block is selected, infer its column.
      if (this.selectedBlockId) {
        for (const r of this.allRows) {
          for (const c of r.columns || []) {
            if ((c.blocks || []).some((b) => b.id === this.selectedBlockId)) return c;
          }
        }
      }
      return null;
    },
    selectedBlock() {
      if (!this.selectedBlockId) return null;
      for (const row of this.allRows) {
        for (const col of row.columns || []) {
          const b = (col.blocks || []).find((x) => x.id === this.selectedBlockId);
          if (b) return b;
        }
      }
      return null;
    },
    canUndo() {
      return this.historyPast.length > 1;
    },
    canRedo() {
      return this.historyFuture.length > 0;
    },

    aiLoadingSentence() {
      const list = Array.isArray(this.aiLoadingSentences) ? this.aiLoadingSentences : [];
      if (!list.length) return this.$_("visualEditor-emailEditor-ai-loadingFallback");
      const idx = Number(this.aiLoadingSentenceIdx) || 0;
      return list[Math.abs(idx) % list.length] || this.$_("visualEditor-emailEditor-ai-loadingFallback");
    },
    canvasStyle() {
      const body = this.doc?.body || {};
      const bgColor = body.backgroundColor || "#ffffff";
      const bgStyles = this.backgroundCssFromSettings(body);
      
      const style = {
        backgroundColor: bgColor,
        padding: `0 ${Number(body.mobilePadding || 0)}px`,
      };
      
      // Apply background image styles if present
      if (bgStyles.backgroundImage) {
        style.backgroundImage = bgStyles.backgroundImage;
        if (bgStyles.backgroundSize) style.backgroundSize = bgStyles.backgroundSize;
        if (bgStyles.backgroundPosition) style.backgroundPosition = bgStyles.backgroundPosition;
        if (bgStyles.backgroundRepeat) style.backgroundRepeat = bgStyles.backgroundRepeat;
      }
      
      return style;
    },
  },
  watch: {
    value: {
      deep: true,
      handler(next) {
        if (!next) return;
        if (next === this.doc) return;
        this.doc = deepClone(next);
        this.ensureContainers();
      },
    },
    doc: {
      deep: true,
      handler() {
        this.scheduleHistorySnapshot();
        this.$emit("input", this.doc);
        this.scheduleHtmlUpdate();
      },
    },
    preview(next) {
      if (next) {
        this.$nextTick(() => this.updatePreviewFrameHeight());
      }
    },
    addElementsTab(next) {
      if (next === "modules") {
        this.loadModules();
      }
    },
    selectedBlockId() {
      if (this.isMobileUi) this.mobileBarOpen = false;
      if (this.isMobileUi) this.mobileToolsExpandedKey = null;
    },
    selectedColId() {
      if (this.isMobileUi) this.mobileBarOpen = false;
      if (this.isMobileUi) this.mobileToolsExpandedKey = null;
    },
    selectedRowId() {
      if (this.isMobileUi) this.mobileBarOpen = false;
      if (this.isMobileUi) this.mobileToolsExpandedKey = null;
    },
    selectedContainerId() {
      if (this.isMobileUi) this.mobileBarOpen = false;
      if (this.isMobileUi) this.mobileToolsExpandedKey = null;
    },
    mobileInsertMode(next) {
      // Keep the mobile insert UX lightweight: when switching modes, cancel any pending insert.
      this.cancelMobilePending();
      if (next === "modules") {
        this.loadModules();
      }
    },
    previewHtml() {
      if (this.preview) {
        // allow iframe to render the new srcdoc before measuring
        this.$nextTick(() => {
          window.setTimeout(() => this.updatePreviewFrameHeight(), 0);
        });
      }
    },
    templateGalleryOpen(next) {
      if (next) this.loadTemplateGallery();
    },
    focusedContainerId(next) {
      if (!next) return;
      this.$nextTick(() => this.applyDragMenuBottomGuardByRef(`containerControls-${next}`));
    },
    focusedRowId(next) {
      if (!next) return;
      this.$nextTick(() => this.applyDragMenuBottomGuardByRef(`rowControls-${next}`));
    },
    focusedColId(next) {
      if (!next) return;
      this.$nextTick(() => this.applyDragMenuBottomGuardByRef(`colControls-${next}`));
    },
    selectedBlockId(next) {
      if (!next) return;
      this.$nextTick(() => this.applyDragMenuBottomGuardByRef(`blockControls-${next}`));
    },

    aiLoading(next) {
      if (next) this.startAiLoaderSentences();
      else this.stopAiLoaderSentences();
    },
  },
  created() {
    this.ensureContainers();
  },
  mounted() {
    this.ensureContainers();
    // Seed history with initial state
    this.historyPast = [this.snapshotDoc(this.doc)];
    window.addEventListener("keydown", this.onKeydown, { passive: false });
    this.scheduleHtmlUpdate();
    // Normalize existing rows so we don't need to mutate during render
    try {
      (this.allRows || []).forEach((r) => this.normalizeRowWeights(r));
    } catch (e) {
      // ignore
    }
    // If nothing is selected initially, automatically open email design settings (desktop only).
    // On small screens, only open when user explicitly clicks the cog.
    const isDesktop =
      (window.matchMedia && window.matchMedia("(min-width: 1200px)").matches) ||
      window.innerWidth >= 1200;
    if (
      isDesktop &&
      !this.selectedBlockId &&
      !this.selectedColId &&
      !this.selectedRowId &&
      !this.selectedContainerId
    ) {
      this.emailDesignOpen = true;
    }

    // When starting a new email (no incoming v-model) or when the doc is effectively empty,
    // open the template gallery by default so the user can pick a starting point.
    if (!this.value || this.isDocEffectivelyEmpty(this.doc)) {
      this.emailDesignOpen = false;
      this.mobileSettingsOpen = false;
      this.templateGalleryOpen = true;
    }

    this.updateMobileUiFlag();
    window.addEventListener("resize", this.updateMobileUiFlag, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.updateMobileUiFlag);
    if (this.historyTimer) window.clearTimeout(this.historyTimer);
    if (this.htmlUpdateTimer) window.clearTimeout(this.htmlUpdateTimer);
    this.stopAiLoaderSentences();
  },
  methods: {
    // -------- Save/Edit assets (templates/modules) --------
    canEditSavedAsset(kind, item) {
      void kind;
      if (!item) return false;
      const isGlobal = item.places_id === null || item.places_id === undefined || item.places_id === 0 || item.places_id === "0";
      if (isGlobal) return this.isGlobalAdmin;
      return this.isGlobalAdmin || Number(item.places_id) === Number(this.currentPlaceId);
    },
    canDeleteSavedAsset(item) {
      if (!item) return false;
      const isGlobal = item.places_id === null || item.places_id === undefined || item.places_id === 0 || item.places_id === "0";
      if (isGlobal) return this.isGlobalAdmin;
      return this.isGlobalAdmin || Number(item.places_id) === Number(this.currentPlaceId);
    },
    openSaveAssetModal(kind = "template", ctx = {}) {
      this.saveAssetMode = "create";
      this.saveAssetKind = kind === "module" ? "module" : "template";
      this.saveAssetEditingItem = null;
      this.saveAssetName = "";
      this.saveAssetVisibility = "place";
      this.saveAssetTargetRow = this.saveAssetKind === "module" ? (ctx?.row || this.selectedRow || null) : null;
      this.saveAssetModalOpen = true;
    },
    openEditSavedAssetModal(kind = "template", item) {
      this.saveAssetMode = "edit";
      this.saveAssetKind = kind === "module" ? "module" : "template";
      this.saveAssetEditingItem = item || null;
      this.saveAssetName = String(item?.name || "");
      this.saveAssetVisibility = item?.places_id ? "place" : "global";
      if (!this.isGlobalAdmin) this.saveAssetVisibility = "place";
      this.saveAssetTargetRow = null;
      this.saveAssetModalOpen = true;
    },
    closeSaveAssetModal() {
      this.saveAssetModalOpen = false;
      this.saveAssetSaving = false;
      this.saveAssetEditingItem = null;
      this.saveAssetTargetRow = null;
      this.saveAssetName = "";
      this.saveAssetVisibility = "place";
      this.saveAssetMode = "create";
      this.saveAssetKind = "template";
    },
    async submitSaveAssetModal() {
      const name = String(this.saveAssetName || "").trim();
      if (!name) return;
      if (this.saveAssetSaving) return;

      this.saveAssetSaving = true;
      try {
        if (this.saveAssetMode === "edit") {
          await this.updateSavedAsset();
        } else if (this.saveAssetKind === "module") {
          await this.saveModuleAsset();
        } else {
          await this.saveTemplateAsset();
        }
        this.closeSaveAssetModal();
      } catch (e) {
        const msg =
          e?.body?.message ||
          e?.data?.message ||
          e?.message ||
          this.$_("visualEditor-emailEditor-notifications-saveError");
        this.$store?.dispatch?.("notification/error", msg);
      } finally {
        this.saveAssetSaving = false;
      }
    },
    async saveTemplateAsset() {
      const name = String(this.saveAssetName || "").trim();
      const makeGlobal = this.isGlobalAdmin && this.saveAssetVisibility === "global";
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-generatingScreenshot"),
      );
      const el = this.$el?.querySelector?.(".kv-ve__email");
      const blob = await this.captureScreenshotBlob(el);
      const screenshotUrl = blob ? await this.uploadScreenshotBlob(blob, `template-${Date.now()}.png`) : null;

      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-savingTemplate"),
      );
      const payload = {
        type: "kveditor:template",
        global: makeGlobal,
        name,
        content: {
          doc: deepClone(this.doc),
          html: this.exportedHtml,
        },
        screenshot: screenshotUrl,
      };
      const options = keviApi.getOptions("kveditor/save", "POST", payload);
      await this.$http(options);
      this.$store?.dispatch?.(
        "notification/success",
        this.$_(
          makeGlobal
            ? "visualEditor-emailEditor-notifications-templateSavedGlobal"
            : "visualEditor-emailEditor-notifications-templateSaved",
        ),
      );
      if (this.templateGalleryOpen) await this.loadTemplateGallery();
    },
    async saveModuleAsset() {
      const name = String(this.saveAssetName || "").trim();
      const makeGlobal = this.isGlobalAdmin && this.saveAssetVisibility === "global";
      const row = this.saveAssetTargetRow || this.selectedRow;
      if (!row?.id) {
        this.$store?.dispatch?.(
          "notification/error",
          this.$_("visualEditor-emailEditor-notifications-selectRowToSaveModule"),
        );
        return;
      }
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-generatingModuleScreenshot"),
      );
      const el = this.$el?.querySelector?.(`[data-kv-ve-row-id="${row.id}"]`);
      const blob = await this.captureScreenshotBlob(el);
      const screenshotUrl = blob ? await this.uploadScreenshotBlob(blob, `module-${Date.now()}.png`) : null;

      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-savingModule"),
      );
      const payload = {
        type: "kveditor:module",
        global: makeGlobal,
        name,
        content: {
          row: deepClone(row),
        },
        screenshot: screenshotUrl,
      };
      const options = keviApi.getOptions("kveditor/save", "POST", payload);
      await this.$http(options);
      this.$store?.dispatch?.(
        "notification/success",
        this.$_(
          makeGlobal
            ? "visualEditor-emailEditor-notifications-moduleSavedGlobal"
            : "visualEditor-emailEditor-notifications-moduleSaved",
        ),
      );
      await this.loadModules(true);
    },
    async updateSavedAsset() {
      const item = this.saveAssetEditingItem;
      if (!item?.id) return;
      const name = String(this.saveAssetName || "").trim();
      const makeGlobal = this.isGlobalAdmin && this.saveAssetVisibility === "global";
      const payload = {
        id: item.id,
        type: this.saveAssetKind === "module" ? "kveditor:module" : "kveditor:template",
        global: makeGlobal,
        name,
        content: item.content,
        mail_subject: item.mail_subject || null,
        screenshot: item.screenshot || null,
      };
      const options = keviApi.getOptions("kveditor/save", "POST", payload);
      await this.$http(options);
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-updated"),
      );
      if (this.saveAssetKind === "module") {
        await this.loadModules(true);
      } else if (this.templateGalleryOpen) {
        await this.loadTemplateGallery();
      }
    },
    async deleteSavedAsset() {
      const item = this.saveAssetEditingItem;
      if (!item?.id) return;
      if (!this.canDeleteSavedAsset(item)) return;
      const ok = window.confirm(this.$_("visualEditor-emailEditor-prompts-confirmDeleteAsset"));
      if (!ok) return;
      if (this.saveAssetSaving) return;
      this.saveAssetSaving = true;
      try {
        const options = keviApi.getOptions(`kveditor/${item.id}`, "DELETE");
        await this.$http(options);
        this.$store?.dispatch?.(
          "notification/success",
          this.$_("visualEditor-emailEditor-notifications-deleted"),
        );
        if (this.saveAssetKind === "module") await this.loadModules(true);
        else if (this.templateGalleryOpen) await this.loadTemplateGallery();
        this.closeSaveAssetModal();
      } finally {
        this.saveAssetSaving = false;
      }
    },
    isMobileToolsExpanded(kind, id) {
      if (!this.isMobileUi) return true;
      if (!kind || !id) return false;
      return this.mobileToolsExpandedKey === `${kind}:${id}`;
    },
    onMobileSettingsIcon(kind, id, tab) {
      if (!this.isMobileUi) return;
      this.openMobileActionMenu(kind, id, tab);
    },
    findBlockById(blockId) {
      for (const c of this.doc?.containers || []) {
        for (const r of c.rows || []) {
          for (const col of r.columns || []) {
            const b = (col.blocks || []).find((x) => x?.id === blockId);
            if (b) return { container: c, row: r, col, block: b };
          }
        }
      }
      return null;
    },
    findContainerById(containerId) {
      return (this.doc?.containers || []).find((c) => c?.id === containerId) || null;
    },
    openMobileActionMenu(kind, id, tab) {
      if (!this.isMobileUi) return;
      if (!kind || !id) return;
      this.mobileBarOpen = false;
      this.mobileToolsExpandedKey = null;

      let ctx = { kind, tab: tab || null };
      if (kind === "block") {
        const hit = this.findBlockById(id);
        if (!hit) return;
        ctx = { ...ctx, ...hit };
      } else if (kind === "col") {
        const hit = this.findColById(id);
        if (!hit) return;
        ctx = { ...ctx, ...hit };
      } else if (kind === "row") {
        const hit = this.findRowById(id);
        if (!hit) return;
        ctx = { ...ctx, ...hit };
      } else if (kind === "section") {
        const container = this.findContainerById(id);
        if (!container) return;
        ctx = { ...ctx, container };
      }
      this.mobileActionMenu = ctx;
    },
    closeMobileActionMenu() {
      this.mobileActionMenu = null;
    },
    mobileActionEditSettings() {
      const tab = this.mobileActionMenu?.tab || null;
      if (tab) this.settingsTab = tab;
      this.emailDesignOpen = false;
      this.mobileSettingsOpen = true;
      this.closeMobileActionMenu();
    },
    mobileActionMove() {
      const m = this.mobileActionMenu;
      if (!m) return;
      if (m.kind === "block") this.startMobileMoveBlock(m.row, m.col, m.block);
      else if (m.kind === "col") this.startMobileMoveCol(m.row, m.col);
      else if (m.kind === "row") this.startMobileMoveRow(m.container, m.row);
      else if (m.kind === "section") this.startMobileMoveSection(m.container);
      this.closeMobileActionMenu();
    },
    mobileActionDuplicate() {
      const m = this.mobileActionMenu;
      if (!m) return;
      if (m.kind === "block") this.duplicateBlock(m.row, m.col, m.block);
      else if (m.kind === "col") this.onDuplicateClickCol(m.col);
      else if (m.kind === "row") this.duplicateRow(m.row);
      else if (m.kind === "section") this.duplicateContainer(m.container);
      this.closeMobileActionMenu();
    },
    mobileActionCopyStyles() {
      const m = this.mobileActionMenu;
      if (!m) return;
      if (m.kind === "block") this.onBrushClickBlock(m.block);
      else if (m.kind === "col") this.onBrushClickCol(m.col);
      else if (m.kind === "row") this.onBrushClickRow(m.row);
      else if (m.kind === "section") this.onBrushClickContainer(m.container);
      this.closeMobileActionMenu();
    },
    mobileActionDelete() {
      const m = this.mobileActionMenu;
      if (!m) return;
      if (m.kind === "block") this.deleteBlock(m.row, m.col, m.block);
      else if (m.kind === "row") this.deleteRow(m.row);
      else if (m.kind === "section") this.deleteContainer(m.container);
      this.closeMobileActionMenu();
    },
    closeMobileSettingsDrawer() {
      this.mobileSettingsOpen = false;
      this.mobileToolsExpandedKey = null;
    },
    updateMobileUiFlag() {
      const mq = window.matchMedia ? window.matchMedia("(max-width: 768px)") : null;
      this.isMobileUi = mq ? mq.matches : (window.innerWidth || 0) <= 768;
      if (!this.isMobileUi) {
        this.mobilePending = null;
        this.mobileMovePending = null;
        this.mobileInsertMode = "blocks";
        this.mobileBarOpen = false;
        this.mobileSettingsOpen = false;
        this.mobileToolsExpandedKey = null;
      }
    },
    async onMobileSelect(payload) {
      const kind = payload?.kind;
      if (!kind) return;

      if (kind === "module") {
        await this.loadModules();
        const module = (this.kveditorModules || []).find((m) => String(m.id) === String(payload.id)) || null;
        if (!module) return;
        this.mobilePending = { kind: "module", id: module.id, module };
        this.mobileBarOpen = false;
        return;
      }

      if (kind === "row") {
        const rowTpl = (this.rowPalette || []).find((r) => r.key === payload.key) || null;
        if (!rowTpl) return;
        this.mobilePending = { kind: "row", key: rowTpl.key, rowTpl };
        this.mobileBarOpen = false;
        return;
      }

      if (kind === "block") {
        this.mobilePending = { kind: "block", type: payload.type };
        this.mobileBarOpen = false;
      }
    },
    cancelMobilePending() {
      this.mobilePending = null;
      this.mobileMovePending = null;
    },
    startMobileMoveBlock(row, col, block) {
      if (!this.isMobileUi) return;
      if (!row?.id || !col?.id || !block?.id) return;
      this.mobilePending = null; // don't mix insert + move
      this.mobileMovePending = {
        kind: "block",
        blockId: block.id,
        fromRowId: row.id,
        fromColId: col.id,
      };
      this.mobileToolsExpandedKey = null;
      this.mobileBarOpen = false;
    },
    startMobileMoveCol(row, col) {
      if (!this.isMobileUi) return;
      if (!row?.id || !col?.id) return;
      this.mobilePending = null;
      this.mobileMovePending = {
        kind: "col",
        rowId: row.id,
        colId: col.id,
      };
      this.mobileToolsExpandedKey = null;
      this.mobileBarOpen = false;
    },
    startMobileMoveRow(container, row) {
      if (!this.isMobileUi) return;
      if (!container?.id || !row?.id) return;
      this.mobilePending = null;
      this.mobileMovePending = {
        kind: "row",
        rowId: row.id,
        fromContainerId: container.id,
      };
      this.mobileToolsExpandedKey = null;
      this.mobileBarOpen = false;
    },
    startMobileMoveSection(container) {
      if (!this.isMobileUi) return;
      if (!container?.id) return;
      this.mobilePending = null;
      this.mobileMovePending = {
        kind: "section",
        containerId: container.id,
      };
      this.mobileToolsExpandedKey = null;
      this.mobileBarOpen = false;
    },
    findColById(colId) {
      for (const c of this.doc?.containers || []) {
        for (const r of c.rows || []) {
          for (const col of r.columns || []) {
            if (col?.id === colId) return { container: c, row: r, col };
          }
        }
      }
      return null;
    },
    findRowById(rowId) {
      for (const c of this.doc?.containers || []) {
        for (const r of c.rows || []) {
          if (r?.id === rowId) return { container: c, row: r };
        }
      }
      return null;
    },
    mobileMoveBlockTo(targetRow, targetCol) {
      if (!this.isMobileUi) return;
      const p = this.mobileMovePending;
      if (!p || p.kind !== "block") return;
      if (!targetRow?.id || !targetCol?.id) return;

      const from = this.findColById(p.fromColId);
      if (!from?.col) return;
      const idx = (from.col.blocks || []).findIndex((b) => b?.id === p.blockId);
      if (idx < 0) return;

      const [block] = from.col.blocks.splice(idx, 1);
      if (!block) return;
      if (!Array.isArray(targetCol.blocks)) this.$set(targetCol, "blocks", []);
      targetCol.blocks.push(block);
      try {
        this.selectBlock(targetRow, targetCol, block);
      } catch (e) {
        // ignore
      }
      this.mobileMovePending = null;
    },
    mobileMoveRowTo(targetContainer) {
      if (!this.isMobileUi) return;
      const p = this.mobileMovePending;
      if (!p || p.kind !== "row") return;
      if (!targetContainer?.id) return;

      const from = this.findRowById(p.rowId);
      if (!from?.container || !from?.row) return;
      const rows = from.container.rows || [];
      const idx = rows.findIndex((r) => r?.id === p.rowId);
      if (idx < 0) return;
      const [row] = rows.splice(idx, 1);
      if (!row) return;

      if (!Array.isArray(targetContainer.rows)) this.$set(targetContainer, "rows", []);
      targetContainer.rows.push(row);
      this.ensureContainerHasAtLeastOneRow(from.container);
      this.ensureContainerHasAtLeastOneRow(targetContainer);
      try {
        this.selectRow(row, targetContainer);
      } catch (e) {
        // ignore
      }
      this.mobileMovePending = null;
    },
    mobileMoveSectionTo(targetContainer) {
      if (!this.isMobileUi) return;
      const p = this.mobileMovePending;
      if (!p || p.kind !== "section") return;
      if (!targetContainer?.id) return;
      if (p.containerId === targetContainer.id) return;

      const list = this.doc?.containers || [];
      const fromIdx = list.findIndex((c) => c?.id === p.containerId);
      const toIdx = list.findIndex((c) => c?.id === targetContainer.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const [moved] = list.splice(fromIdx, 1);
      if (!moved) return;
      const insertIdx = fromIdx < toIdx ? toIdx : toIdx + 1;
      list.splice(insertIdx, 0, moved);
      this.mobileMovePending = null;
    },
    mobileMoveColTo(row, targetCol) {
      if (!this.isMobileUi) return;
      const p = this.mobileMovePending;
      if (!p || p.kind !== "col") return;
      if (!row?.id || row.id !== p.rowId) return;
      if (!targetCol?.id) return;
      if (p.colId === targetCol.id) return;

      const cols = row.columns || [];
      const i = cols.findIndex((c) => c?.id === p.colId);
      const j = cols.findIndex((c) => c?.id === targetCol.id);
      if (i < 0 || j < 0) return;

      // Swap columns
      const tmp = cols[i];
      this.$set(cols, i, cols[j]);
      this.$set(cols, j, tmp);

      // Swap weights if present
      if (row?.settings?.columnWeights && Array.isArray(row.settings.columnWeights) && row.settings.columnWeights.length === cols.length) {
        const w = row.settings.columnWeights;
        const wt = w[i];
        this.$set(w, i, w[j]);
        this.$set(w, j, wt);
      }
      this.normalizeRowWeights(row);
      this.mobileMovePending = null;
    },
    mobileApplyBlock(row, col) {
      if (!this.isMobileUi) return;
      if (!this.mobilePending || this.mobilePending.kind !== "block") return;
      const type = this.mobilePending.type;
      if (!type) return;

      const block = this.clonePaletteBlock({ type });
      if (!block) return;
      if (!Array.isArray(col?.blocks)) this.$set(col, "blocks", []);
      col.blocks.push(block);
      try {
        this.selectBlock(row, col, block);
      } catch (e) {
        // ignore selection edge cases
      }
      this.mobilePending = null;
    },
    mobileApplyRow(container) {
      if (!this.isMobileUi) return;
      if (!this.mobilePending) return;
      if (!container) return;
      if (!Array.isArray(container.rows)) this.$set(container, "rows", []);

      let row = null;
      if (this.mobilePending.kind === "row") {
        row = this.cloneRowTemplate(this.mobilePending.rowTpl);
      } else if (this.mobilePending.kind === "module") {
        row = this.cloneModule(this.mobilePending.module);
      }
      if (!row) return;

      container.rows.push(row);
      this.ensureContainerHasAtLeastOneRow(container);
      try {
        this.selectRow(row, container);
      } catch (e) {
        // ignore
      }
      this.mobilePending = null;
    },
    backgroundCssFromSettings(s = {}) {
      const mode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
      const img = s.backgroundImageUrl || null;
      const isImage = mode === "image" && img;
      if (!isImage) {
        return {
          backgroundImage: null,
          backgroundSize: null,
          backgroundPosition: null,
          backgroundRepeat: null,
        };
      }

      const sizeModeRaw = String(s.backgroundSizeMode || "").toLowerCase();
      const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
      const sizeUnitRaw = String(s.backgroundSizeUnit || "").toLowerCase();
      const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
      const px = Number(s.backgroundSizePx);
      const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
      const pct = Number(s.backgroundSizePct);
      const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
      const repeatRaw = String(s.backgroundRepeat || "").toLowerCase();
      const repeat =
        repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
          ? repeatRaw
          : "no-repeat";
      const position = String(s.backgroundPosition || "center center");

      const backgroundSize =
        sizeMode === "cover" || sizeMode === "contain"
          ? sizeMode
          : (sizeUnit === "px" ? `${sizePx}px auto` : `${sizePct}% auto`);

      return {
        backgroundImage: `url("${img}")`,
        backgroundSize,
        backgroundPosition: position,
        backgroundRepeat: repeat,
      };
    },
    templateThumbStyle(t) {
      const url = t?.screenshot || "";
      return url ? { backgroundImage: `url(${url})` } : {};
    },
    async loadTemplateGallery() {
      this.templateGalleryLoading = true;
      try {
        const tplRes = await this.$http(keviApi.getOptions("kveditor/templates", "GET"));
        const tplData = tplRes?.body?.data || tplRes?.data?.data || [];
        this.kveditorTemplates = Array.isArray(tplData) ? tplData : [];
      } catch (e) {
        this.kveditorTemplates = [];
      } finally {
        this.templateGalleryLoading = false;
      }
    },
    async loadModules(force = false) {
      if (!force && this.kveditorModules.length > 0) return; // Already loaded
      this.modulesLoading = true;
      try {
        const modRes = await this.$http(keviApi.getOptions("kveditor/modules", "GET"));
        const modData = modRes?.body?.data || modRes?.data?.data || [];
        this.kveditorModules = Array.isArray(modData) ? modData : [];
      } catch (e) {
        this.kveditorModules = [];
      } finally {
        this.modulesLoading = false;
      }
    },
    safeJsonParse(str) {
      try {
        return JSON.parse(str);
      } catch (e) {
        return null;
      }
    },
    onTemplateGalleryItemClick(tpl) {
      if (tpl?.__blank) {
        this.doc = createBlankEmailDoc();
        this.ensureContainers();
        this.templateGalleryOpen = false;
        this.$store?.dispatch?.(
          "notification/success",
          this.$_("visualEditor-emailEditor-notifications-startBlank"),
        );
        return;
      }
      this.applyTemplateFromGallery(tpl);
    },
    applyTemplateFromGallery(tpl) {
      const raw = tpl?.content;
      const parsed = typeof raw === "string" ? this.safeJsonParse(raw) : raw;
      const nextDoc = parsed?.doc || null;
      if (!nextDoc) {
        this.$store?.dispatch?.(
          "notification/error",
          this.$_("visualEditor-emailEditor-notifications-invalidTemplateNoDoc"),
        );
        return;
      }
      this.doc = deepClone(nextDoc);
      this.ensureContainers();
      this.templateGalleryOpen = false;
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-templateApplied"),
      );
    },
    rekeyRowForInsert(row) {
      const r = deepClone(row);
      r.id = kvVeId("row");
      r.__isDragClone = false;
      r.__dragCloneOf = null;
      r.columns = (r.columns || []).map((c) => ({
        ...c,
        id: kvVeId("col"),
        blocks: (c.blocks || []).map((b) => ({ ...b, id: kvVeId("block") })),
      }));
      this.normalizeRowWeights(r);
      return r;
    },
    applyModuleFromGallery(mod) {
      const raw = mod?.content;
      const parsed = typeof raw === "string" ? this.safeJsonParse(raw) : raw;
      const row = parsed?.row || null;
      if (!row) {
        this.$store?.dispatch?.(
          "notification/error",
          this.$_("visualEditor-emailEditor-notifications-invalidModuleNoRow"),
        );
        return;
      }
      const c = this.getActiveContainer();
      if (!c) return;
      if (!Array.isArray(c.rows)) this.$set(c, "rows", []);
      const newRow = this.rekeyRowForInsert(row);
      c.rows.push(newRow);
      this.selectRow(newRow, c);
      this.templateGalleryOpen = false;
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-moduleInserted"),
      );
    },
    async elementToPngBlob(el) {
      if (!el) return null;

      // Clone offscreen so we can rewrite image URLs to a same-origin proxy (avoids CORS issues)
      const clone = el.cloneNode(true);
      clone.style.position = "fixed";
      clone.style.left = "-10000px";
      clone.style.top = "0";
      clone.style.width = `${el.getBoundingClientRect().width}px`;
      clone.style.background = "#ffffff";
      clone.setAttribute("data-kv-ve-screenshot-clone", "1");
      document.body.appendChild(clone);

      try {
        await this.rewriteMediaUrlsForScreenshot(clone);
        // Wait a tick so images have a chance to paint
        await new Promise((r) => window.setTimeout(r, 50));

        const canvas = await html2canvas(clone, {
          backgroundColor: "#ffffff",
          useCORS: true,
          allowTaint: false,
          scale: 1,
        });
        return await new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
        });
      } finally {
        document.body.removeChild(clone);
      }
    },
    async captureScreenshotBlob(el) {
      if (!el) return null;
      const prev = {
        selectedRowId: this.selectedRowId,
        selectedColId: this.selectedColId,
        selectedBlockId: this.selectedBlockId,
        selectedContainerId: this.selectedContainerId,
        settingsTab: this.settingsTab,
        emailDesignOpen: this.emailDesignOpen,
      };
      this.clearSelection();
      await this.$nextTick();
      try {
        return await this.elementToPngBlob(el);
      } finally {
        this.selectedRowId = prev.selectedRowId;
        this.selectedColId = prev.selectedColId;
        this.selectedBlockId = prev.selectedBlockId;
        this.selectedContainerId = prev.selectedContainerId;
        this.settingsTab = prev.settingsTab;
        this.emailDesignOpen = prev.emailDesignOpen;
        await this.$nextTick();
      }
    },
    kveditorProxyImageUrl(originalUrl) {
      const u = String(originalUrl || "").trim();
      if (!u) return "";
      const token = this.$store?.state?.user?.token || "";
      const base = keviApi.getApiEndpoint("kveditor/proxy-image");
      return `${base}?access_token=${encodeURIComponent(token)}&url=${encodeURIComponent(u)}`;
    },
    async rewriteMediaUrlsForScreenshot(rootEl) {
      if (!rootEl) return;

      // Rewrite <img src="...">
      const imgs = Array.from(rootEl.querySelectorAll("img"));
      imgs.forEach((img) => {
        const src = img.getAttribute("src") || "";
        if (!src) return;
        if (src.startsWith("data:")) return;
        const proxied = this.kveditorProxyImageUrl(src);
        if (!proxied) return;
        img.setAttribute("crossorigin", "anonymous");
        img.setAttribute("src", proxied);
      });

      // Rewrite inline background-image: url("...")
      const all = Array.from(rootEl.querySelectorAll("*"));
      const bgUrlRe = /url\((['"]?)(.*?)\1\)/i;
      all.forEach((node) => {
        const bg = node.style && node.style.backgroundImage ? String(node.style.backgroundImage) : "";
        if (!bg) return;
        const m = bg.match(bgUrlRe);
        const url = m && m[2] ? String(m[2]) : "";
        if (!url || url.startsWith("data:")) return;
        const proxied = this.kveditorProxyImageUrl(url);
        if (!proxied) return;
        node.style.backgroundImage = `url("${proxied}")`;
      });

      // Best-effort wait for images to load
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) return resolve(true);
              img.onload = () => resolve(true);
              img.onerror = () => resolve(true);
            }),
        ),
      );
    },
    uploadScreenshotBlob(blob, filename = "screenshot.png") {
      return new Promise((resolve, reject) => {
        if (!blob) {
          reject(new Error("no-blob"));
          return;
        }

        const formData = new FormData();
        formData.append("image", blob, filename);

        const url = keviApi.getOptions("kveditor/upload-screenshot", "POST").url;
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        xhr.setRequestHeader("Authorization", keviApi.getAuthHeader().Authorization);

        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || "{}");
            if (xhr.status >= 200 && xhr.status < 300 && json?.value) resolve(json.value);
            else reject(new Error(json?.message || "upload-failed"));
          } catch (e) {
            reject(e);
          }
        };
        xhr.onerror = () => reject(new Error("upload-network-error"));
        xhr.send(formData);
      });
    },
    // NOTE: legacy "save as global" methods removed in favor of the unified save modal.
    ensureContainerHasAtLeastOneRow(container) {
      if (!container) return;
      if (!Array.isArray(container.rows)) this.$set(container, "rows", []);
      if (container.rows.length > 0) return;
      const row = createRow({ kind: "custom", columns: 1 });
      this.normalizeRowWeights(row);
      container.rows.push(row);
    },
    // -------- Style clipboard (copy / paste settings) --------
    clearStyleClipboard() {
      this.styleClipboard = null;
    },
    blockStyleKeys(type) {
      const t = String(type || "").toLowerCase();
      const commonMargins = ["marginTop", "marginRight", "marginBottom", "marginLeft"];
      if (t === "image") {
        return [
          ...commonMargins,
          "widthMode",
          "widthPct",
          "widthPx",
          "display",
          "align",
          "borderRadius",
          "aspectRatio",
          "objectFit",
        ];
      }
      if (t === "button") {
        return [
          ...commonMargins,
          "align",
          "width",
          "backgroundColor",
          "color",
          "border",
          "borderRadius",
          "paddingX",
          "paddingY",
        ];
      }
      if (t === "divider") {
        return [
          ...commonMargins,
          "color",
          "thickness",
          "widthMode",
          "widthPct",
          "widthPx",
        ];
      }
      if (t === "spacer") {
        return [...commonMargins, "height"];
      }
      // text + fallback
      return [...commonMargins, "align"];
    },
    copyStyleFromBlock(block) {
      if (!block?.id) return;
      const type = String(block.type || "").toLowerCase();
      const keys = this.blockStyleKeys(type);
      const s = block.settings || {};
      const styleSettings = {};
      keys.forEach((k) => {
        styleSettings[k] = s[k];
      });
      this.styleClipboard = {
        kind: "block",
        blockType: type,
        sourceId: block.id,
        settings: deepClone(styleSettings),
      };
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-stylePasteBlockHint"),
      );
    },
    applyStyleToBlock(block) {
      if (!this.styleClipboardActive) return false;
      const clip = this.styleClipboard;
      if (clip.kind !== "block") return false;
      if (!block?.id) return false;
      const type = String(block.type || "").toLowerCase();
      if (type !== clip.blockType) return false;

      if (!block.settings) this.$set(block, "settings", {});
      const keys = this.blockStyleKeys(type);
      keys.forEach((k) => {
        const v = clip.settings?.[k];
        if (v === undefined || v === null) this.$delete(block.settings, k);
        else this.$set(block.settings, k, deepClone(v));
      });
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-stylesPasted"),
      );
      this.clearStyleClipboard();
      return true;
    },
    copyStyleFromRow(row) {
      if (!row?.id) return;
      const settings = deepClone(row.settings || {});
      this.styleClipboard = { kind: "row", sourceId: row.id, settings };
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-stylePasteRowHint"),
      );
    },
    applyStyleToRow(row) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "row") return false;
      if (!row?.id) return false;
      this.$set(row, "settings", deepClone(clip.settings || {}));
      this.normalizeRowWeights(row);
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-stylesPasted"),
      );
      this.clearStyleClipboard();
      return true;
    },
    copyStyleFromContainer(container) {
      if (!container?.id) return;
      const settings = deepClone(container.settings || {});
      this.styleClipboard = { kind: "container", sourceId: container.id, settings };
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-stylePasteSectionHint"),
      );
    },
    applyStyleToContainer(container) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "container") return false;
      if (!container?.id) return false;
      this.$set(container, "settings", deepClone(clip.settings || {}));
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-stylesPasted"),
      );
      this.clearStyleClipboard();
      return true;
    },
    copyStyleFromCol(col) {
      if (!col?.id) return;
      const settings = deepClone(col.settings || {});
      this.styleClipboard = { kind: "column", sourceId: col.id, settings };
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-stylePasteColumnHint"),
      );
    },
    applyStyleToCol(row, col) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "column") return false;
      if (!row?.id || !col?.id) return false;
      if (col.id === clip.sourceId) return false;
      this.$set(col, "settings", deepClone(clip.settings || {}));
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-stylesPasted"),
      );
      this.clearStyleClipboard();
      return true;
    },
    copyDuplicateFromCol(col) {
      if (!col?.id) return;
      this.styleClipboard = {
        kind: "column-duplicate",
        sourceId: col.id,
        settings: deepClone(col.settings || {}),
        blocks: deepClone(col.blocks || []),
      };
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-duplicateColumnHint"),
      );
    },
    applyDuplicateToCol(row, col) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "column-duplicate") return false;
      if (!row?.id || !col?.id) return false;
      if (col.id === clip.sourceId) return false;

      // Replace target column content (keep the same column id/slot)
      this.$set(col, "settings", deepClone(clip.settings || {}));
      const nextBlocks = (clip.blocks || []).map((b) => ({ ...deepClone(b), id: kvVeId("block") }));
      this.$set(col, "blocks", nextBlocks);

      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-columnDuplicated"),
      );
      this.clearStyleClipboard();
      return true;
    },
    isStylePasteTargetBlock(block) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "block") return false;
      if (!block?.id) return false;
      if (block.id === clip.sourceId) return false;
      return String(block.type || "").toLowerCase() === String(clip.blockType || "").toLowerCase();
    },
    isStylePasteTargetRow(row) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "row") return false;
      if (!row?.id) return false;
      return row.id !== clip.sourceId;
    },
    isStylePasteTargetContainer(container) {
      const clip = this.styleClipboard;
      if (!clip || clip.kind !== "container") return false;
      if (!container?.id) return false;
      return container.id !== clip.sourceId;
    },
    isStylePasteTargetCol(col) {
      const clip = this.styleClipboard;
      if (!clip || (clip.kind !== "column" && clip.kind !== "column-duplicate")) return false;
      if (!col?.id) return false;
      return col.id !== clip.sourceId;
    },
    isStyleClipboardSourceBlock(block) {
      const clip = this.styleClipboard;
      return Boolean(clip && clip.kind === "block" && block?.id && clip.sourceId === block.id);
    },
    isStyleClipboardSourceRow(row) {
      const clip = this.styleClipboard;
      return Boolean(clip && clip.kind === "row" && row?.id && clip.sourceId === row.id);
    },
    isStyleClipboardSourceContainer(container) {
      const clip = this.styleClipboard;
      return Boolean(clip && clip.kind === "container" && container?.id && clip.sourceId === container.id);
    },
    isStyleClipboardSourceCol(col) {
      const clip = this.styleClipboard;
      return Boolean(clip && (clip.kind === "column" || clip.kind === "column-duplicate") && col?.id && clip.sourceId === col.id);
    },
    onBrushClickCol(col) {
      const clip = this.styleClipboard;
      if (clip && clip.kind === "column" && this.isStyleClipboardSourceCol(col)) {
        this.clearStyleClipboard();
        return;
      }
      this.copyStyleFromCol(col);
    },
    onDuplicateClickCol(col) {
      const clip = this.styleClipboard;
      if (clip && clip.kind === "column-duplicate" && this.isStyleClipboardSourceCol(col)) {
        this.clearStyleClipboard();
        return;
      }
      this.copyDuplicateFromCol(col);
    },
    onBrushClickBlock(block) {
      if (this.isStyleClipboardSourceBlock(block)) {
        this.clearStyleClipboard();
        return;
      }
      this.copyStyleFromBlock(block);
    },
    onBrushClickRow(row) {
      if (this.isStyleClipboardSourceRow(row)) {
        this.clearStyleClipboard();
        return;
      }
      this.copyStyleFromRow(row);
    },
    onBrushClickContainer(container) {
      if (this.isStyleClipboardSourceContainer(container)) {
        this.clearStyleClipboard();
        return;
      }
      this.copyStyleFromContainer(container);
    },
    copyStyleFromSelectedCol() {
      const col = this.selectedCol;
      if (!col?.id) return;
      this.copyStyleFromCol(col);
    },
    copyDuplicateFromSelectedCol() {
      const col = this.selectedCol;
      if (!col?.id) return;
      this.copyDuplicateFromCol(col);
    },
    toggleEmailDesignOpen() {
      this.emailDesignOpen = !this.emailDesignOpen;
    },
    save() {
      // v-model already emits changes; this is an explicit "commit" action for the caller.
      this.$emit("save", { doc: deepClone(this.doc), html: this.exportedHtml });
      this.$store?.dispatch?.(
        "notification/success",
        this.$_("visualEditor-emailEditor-notifications-saved"),
      );
    },
    sendTestEmail() {
      const to = window.prompt(this.$_("visualEditor-emailEditor-prompts-sendTestEmail"), "") || "";
      const email = String(to).trim();
      if (!email) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.$store?.dispatch?.(
          "notification/error",
          this.$_("visualEditor-emailEditor-notifications-invalidEmail"),
        );
        return;
      }
      this.$emit("send-test", { to: email, doc: deepClone(this.doc), html: this.exportedHtml });
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-sendingTestEmail", [email]),
      );
    },
    clearHoveredRow() {
      this.hoveredRowId = null;
    },
    clearHoveredContainer() {
      this.hoveredContainerId = null;
    },
    ensureContainers() {
      if (!this.doc) return;
      if (Array.isArray(this.doc.containers)) {
        // Ensure nested defaults exist
        this.doc.containers.forEach((c) => {
          if (!Array.isArray(c.rows)) this.$set(c, "rows", []);
          if (!c.settings) this.$set(c, "settings", {});
          this.ensureContainerHasAtLeastOneRow(c);
        });
        return;
      }
      const rows = Array.isArray(this.doc.rows) ? this.doc.rows : [];
      const bodyContainer = createContainer({ kind: "body", title: "Body", rows });
      this.$set(this.doc, "containers", [bodyContainer]);
      // Keep older schema key around but empty to avoid duplication.
      this.$set(this.doc, "rows", []);
      this.ensureContainerHasAtLeastOneRow(bodyContainer);
    },
    isDocEffectivelyEmpty(doc) {
      const containers = Array.isArray(doc?.containers) ? doc.containers : [];
      for (const c of containers) {
        for (const r of c?.rows || []) {
          for (const col of r?.columns || []) {
            if ((col?.blocks || []).length) return false;
          }
        }
      }
      return true;
    },
    findContainerByRowId(rowId) {
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      for (const c of containers) {
        if ((c?.rows || []).some((r) => r?.id === rowId)) return c;
      }
      return null;
    },
    getActiveContainer() {
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      if (!containers.length) return null;
      if (this.selectedRowId) {
        const c = this.findContainerByRowId(this.selectedRowId);
        if (c) return c;
      }
      if (this.selectedContainerId) {
        const c = containers.find((x) => x.id === this.selectedContainerId);
        if (c) return c;
      }
      return containers[0];
    },
    disableTextSelection() {
      try {
        const el = document.documentElement;
        if (!el) return;
        if (this.prevDocUserSelect === null) this.prevDocUserSelect = el.style.userSelect || "";
        el.style.userSelect = "none";
      } catch (e) {
        // ignore
      }
    },
    restoreTextSelection() {
      try {
        const el = document.documentElement;
        if (!el) return;
        if (this.prevDocUserSelect === null) return;
        el.style.userSelect = this.prevDocUserSelect;
        this.prevDocUserSelect = null;
      } catch (e) {
        // ignore
      }
    },
    onBlockTouchEnd(row, col, block, e) {
      // Mobile-only: implement double-tap for images (gallery) and other blocks (settings).
      if (!this.isMobileUi) return;
      if (!block || !block.id) return;

      const now = Date.now();
      const lastTs = Number(this.mobileLastTapTs) || 0;
      const lastId = this.mobileLastTapBlockId;
      const isDoubleTap = lastId === block.id && now - lastTs > 0 && now - lastTs < 320;

      this.mobileLastTapTs = now;
      this.mobileLastTapBlockId = block.id;

      if (!isDoubleTap) return;

      // Prevent the second tap from triggering other handlers.
      try {
        e?.preventDefault?.();
        e?.stopPropagation?.();
      } catch (err) {
        // ignore
      }

      // For images, open gallery; for all other blocks, open settings directly.
      if (block.type === "image") {
        this.openImageGallery(row, col, block);
      } else {
        // Open settings sidebar directly (shortcut)
        this.settingsTab = "block";
        this.emailDesignOpen = false;
        this.mobileSettingsOpen = true;
        this.closeMobileActionMenu();
      }
    },
    onBlockDblClick(row, col, block) {
      if (!block || block.type !== "image") return;
      this.openImageGallery(row, col, block);
    },

    openImageGallery(row, col, block) {
      // Ensure the block is selected (so sidebar settings reflect the same block)
      this.selectBlock(row, col, block);
      this.imageGalleryTarget = block;
      this.imageGalleryOpen = true;
    },

    onImagePickedFromGallery(file) {
      if (!file || !file.url) return;
      const target = this.imageGalleryTarget;
      if (!target) return;

      if (!target.settings) target.settings = {};
      target.settings.src = file.url;

      // modal auto-closes, but keep state consistent
      this.imageGalleryOpen = false;
      this.imageGalleryTarget = null;
    },

    onImageGalleryClosed() {
      this.imageGalleryOpen = false;
      this.imageGalleryTarget = null;
    },

    onAnyDragStart(group = null, evt = null) {
      this.isDragging = true;
      this.disableTextSelection();
      this.dragGroup = group === "rows" || group === "blocks" || group === "containers" ? group : null;
      // If user starts dragging anything, cancel style-paste mode (it uses pointer-events overrides
      // that can interfere with Sortable drop targets).
      if (this.styleClipboardActive) this.clearStyleClipboard();
      // Hide any selection UI while dragging (selection state can remain, but we also clear it)
      this.clearSelection();
    },
    onAnyDragEnd() {
      this.isDragging = false;
      this.dragGroup = null;
      this.restoreTextSelection();
    },
    scheduleHtmlUpdate() {
      if (this.htmlUpdateTimer) window.clearTimeout(this.htmlUpdateTimer);
      this.htmlUpdateTimer = window.setTimeout(() => {
        this.htmlUpdateTimer = null;
        this.$emit("html-update", this.exportedHtml);
      }, 250);
    },
    updatePreviewFrameHeight() {
      if (!this.preview) return;
      const frame = this.$refs.previewFrame;
      if (!frame) return;
      try {
        const doc = frame.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement?.scrollHeight || 0,
          doc.body?.scrollHeight || 0,
        );
        const containerH = frame.parentElement?.clientHeight || 0;
        const finalH = Math.max(h, containerH, 200);
        frame.style.height = `${finalH}px`;
      } catch (e) {
        // Ignore: sandbox/cross-origin restrictions (shouldn't happen with srcdoc).
      }
    },
    snapshotDoc(doc) {
      // Keep snapshots stable; JSON stringify is acceptable at this scale.
      return JSON.stringify(doc);
    },
    restoreSnapshot(json) {
      this.isRestoringHistory = true;
      try {
        this.doc = JSON.parse(json);
        this.clearSelection();
      } finally {
        // Let Vue settle before re-enabling history capture
        this.$nextTick(() => {
          this.isRestoringHistory = false;
        });
      }
    },
    scheduleHistorySnapshot() {
      if (this.isRestoringHistory) return;
      if (this.historyTimer) window.clearTimeout(this.historyTimer);
      // Debounce to coalesce rapid changes (typing, dragging).
      this.historyTimer = window.setTimeout(() => {
        this.historyTimer = null;
        if (this.isRestoringHistory) return;
        const snap = this.snapshotDoc(this.doc);
        const last = this.historyPast[this.historyPast.length - 1];
        if (snap === last) return;
        this.historyPast.push(snap);
        // Any new change invalidates redo stack
        this.historyFuture = [];
        // Prevent unbounded growth
        if (this.historyPast.length > 80) {
          this.historyPast = this.historyPast.slice(this.historyPast.length - 80);
        }
      }, 300);
    },
    undo() {
      if (!this.canUndo) return;
      const current = this.historyPast[this.historyPast.length - 1];
      const prev = this.historyPast[this.historyPast.length - 2];
      this.historyPast = this.historyPast.slice(0, -1);
      this.historyFuture.unshift(current);
      this.restoreSnapshot(prev);
    },
    redo() {
      if (!this.canRedo) return;
      const next = this.historyFuture[0];
      const current = this.historyPast[this.historyPast.length - 1];
      this.historyFuture = this.historyFuture.slice(1);
      this.historyPast.push(next);
      this.restoreSnapshot(next);
      // keep current already in past; no-op
      void current;
    },
    isTypingTarget(target) {
      if (!target) return false;
      const el = target.closest?.("input, textarea, select");
      if (el) return true;
      // TipTap uses contenteditable div
      if (target.closest?.(".ProseMirror")) return true;
      if (target.isContentEditable) return true;
      return false;
    },
    onKeydown(e) {
      const keyRaw = String(e.key || "");
      const key = keyRaw.toLowerCase();

      // Unselect everything with Escape.
      if (keyRaw === "Escape") {
        if (this.styleClipboardActive) {
          e.preventDefault();
          this.clearStyleClipboard();
          this.$store?.dispatch?.(
            "notification/info",
            this.$_("visualEditor-emailEditor-notifications-styleCopyCancelled"),
          );
          return;
        }
        if (this.selectedBlockId || this.selectedColId || this.selectedRowId || this.selectedContainerId) {
          e.preventDefault();
          this.clearSelection();
        }
        return;
      }

      if (this.isTypingTarget(e.target)) return;
      if (this.isDragging) return;

      // Delete selected node (block/column/row/container) with Backspace/Delete.
      if (keyRaw === "Backspace" || keyRaw === "Delete") {
        if (this.selectedBlockId || this.selectedColId || this.selectedRowId || this.selectedContainerId) {
          e.preventDefault(); // prevent browser navigation/back
          if (this.selectedBlockId) this.deleteSelectedBlock();
          else if (this.selectedColId) this.deleteSelectedCol();
          else if (this.selectedRowId) this.deleteSelectedRow();
          else if (this.selectedContainerId) this.deleteSelectedContainer();
        }
        return;
      }

      // Undo / redo (Cmd/Ctrl + Z/Y)
      const isMac = navigator.platform?.toUpperCase?.().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;

      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) this.redo();
        else this.undo();
      } else if (key === "y") {
        e.preventDefault();
        this.redo();
      }
    },
    onLocalKeydown(e) {
      // Important: prevent Esc from bubbling to the outer automation modal, which closes on Escape.
      // When editing emails, Esc should only clear selection (handled by `onKeydown`).
      if (e && e.key === "Escape") {
        this.onKeydown(e);
        e.stopPropagation();
      }
    },
    blockDesc(type) {
      const map = {
        text: "Rich text with toolbar",
        image: "Image with optional link",
        button: "Call-to-action button",
        divider: "Horizontal divider line",
        spacer: "Adjust vertical spacing",
      };
      return map[type] || "";
    },
    sectionDesc(key) {
      const map = {
        hero_center: "Title, text and centered CTA",
        image_text: "Two columns: image + copy",
        divider: "Full-width divider row",
      };
      return map[key] || "Prebuilt layout";
    },
    paletteLabel(type) {
      const found = this.blockPalette.find((b) => b.type === type);
      if (found?.labelKey) return this.$_(found.labelKey);
      return type;
    },
    rowPalettePreviewStyle(item) {
      const w = Array.isArray(item?.weights) ? item.weights : [];
      if (w.length >= 2) {
        return {
          gridTemplateColumns: w.map((x) => `${Number(x) || 1}fr`).join(" "),
        };
      }
      return { "--kv-ve-cols": item.columns };
    },
    getRowWeights(row) {
      const cols = row?.columns || [];
      const n = Math.max(1, cols.length);
      const raw = row?.settings?.columnWeights;
      return Array.from({ length: n }, (_, i) => {
        const v = Number(raw?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
    },
    normalizeRowWeights(row) {
      if (!row) return;
      const cols = row?.columns || [];
      const n = Math.max(1, cols.length);
      const raw = row?.settings?.columnWeights;
      const next = Array.from({ length: n }, (_, i) => {
        const v = Number(raw?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
      if (!row.settings) this.$set(row, "settings", {});
      // Only write if needed (avoid needless reactivity churn)
      const cur = row.settings.columnWeights;
      const same =
        Array.isArray(cur) &&
        cur.length === next.length &&
        cur.every((x, i) => Number(x) === Number(next[i]));
      if (!same) this.$set(row.settings, "columnWeights", next);
    },
    columnWidthPct(row, colIdx) {
      const weights = this.getRowWeights(row);
      const total = weights.reduce((a, b) => a + b, 0) || weights.length;
      const pcts = weights.map((w) => Math.max(1, Math.floor((w / total) * 100)));
      const sum = pcts.reduce((a, b) => a + b, 0);
      pcts[pcts.length - 1] = Math.max(1, pcts[pcts.length - 1] + (100 - sum));
      return pcts[colIdx] ?? Math.floor(100 / Math.max(1, weights.length));
    },
    isRowLayoutActive(row, opt) {
      const n = row?.columns?.length || 0;
      if (n !== opt.columns) return false;
      const w = this.getRowWeights(row);
      const ow = Array.isArray(opt.weights) ? opt.weights : Array.from({ length: opt.columns }, () => 1);
      if (ow.length !== w.length) return false;
      for (let i = 0; i < w.length; i += 1) {
        if (Number(w[i]) !== Number(ow[i])) return false;
      }
      return true;
    },
    blockComponent(type) {
      switch (type) {
        case "text":
          return "VeTextBlock";
        case "image":
          return "VeImageBlock";
        case "button":
          return "VeButtonBlock";
        case "divider":
          return "VeDividerBlock";
        case "spacer":
          return "VeSpacerBlock";
        default:
          return "VeTextBlock";
      }
    },
    clearSelection() {
      this.selectedRowId = null;
      this.selectedColId = null;
      this.selectedBlockId = null;
      this.selectedContainerId = null;
    },
    openGlobalSettings() {
      this.clearSelection();
      // When nothing is selected, automatically open email design settings
      this.emailDesignOpen = true;
    },
    closeSettingsOverlay() {
      this.clearSelection();
      // When nothing is selected, automatically open email design settings
      this.emailDesignOpen = true;
    },
    selectContainer(container) {
      if (!container?.id) return;
      if (this.styleClipboardActive && this.applyStyleToContainer(container)) return;
      this.selectedContainerId = container.id;
      this.selectedRowId = null;
      this.selectedColId = null;
      this.selectedBlockId = null;
      this.settingsTab = "section";
      // When something is selected, close email design settings
      this.emailDesignOpen = false;
    },
    selectRow(row, container = null) {
      if (this.styleClipboardActive && this.applyStyleToRow(row)) {
        // keep selection consistent with clicked row after paste
        this.selectedRowId = row.id;
        this.selectedContainerId = container?.id || this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
        this.selectedColId = null;
        this.selectedBlockId = null;
        this.settingsTab = "row";
        this.emailDesignOpen = false;
        return;
      }
      this.selectedRowId = row.id;
      this.selectedContainerId = container?.id || this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
      this.selectedColId = null;
      this.selectedBlockId = null;
      this.normalizeRowWeights(row);
      this.settingsTab = "row";
      // When something is selected, close email design settings
      this.emailDesignOpen = false;
    },
    selectCol(row, col) {
      if (!row?.id || !col?.id) return;
      if (this.styleClipboardActive) {
        if (this.applyStyleToCol(row, col) || this.applyDuplicateToCol(row, col)) {
          this.selectedRowId = row.id;
          this.selectedContainerId = this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
          this.selectedColId = col.id;
          this.selectedBlockId = null;
          this.settingsTab = "column";
          this.emailDesignOpen = false;
          return;
        }
      }
      this.selectedRowId = row.id;
      this.selectedContainerId = this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
      this.selectedColId = col.id;
      this.selectedBlockId = null;
      this.normalizeRowWeights(row);
      this.settingsTab = "column";
      // When something is selected, close email design settings
      this.emailDesignOpen = false;
    },
    selectBlock(row, col, block) {
      if (this.styleClipboardActive && this.applyStyleToBlock(block)) {
        // keep selection on target
        this.selectedRowId = row.id;
        this.selectedContainerId = this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
        this.selectedColId = col.id;
        this.selectedBlockId = block.id;
        this.settingsTab = "block";
        this.emailDesignOpen = false;
        return;
      }
      this.selectedRowId = row.id;
      this.selectedContainerId = this.findContainerByRowId(row.id)?.id || this.selectedContainerId;
      this.selectedColId = col.id;
      this.selectedBlockId = block.id;
      this.normalizeRowWeights(row);
      this.settingsTab = "block";
      // When something is selected, close email design settings
      this.emailDesignOpen = false;
    },
    hexToCssColor(hex) {
      if (!hex || typeof hex !== "string") return hex;
      const h = hex.trim();
      // If 8-digit hex (#RRGGBBAA), convert to rgba()
      if (h.length === 9 && h.startsWith("#")) {
        const r = parseInt(h.substring(1, 3), 16);
        const g = parseInt(h.substring(3, 5), 16);
        const b = parseInt(h.substring(5, 7), 16);
        const a = parseInt(h.substring(7, 9), 16) / 255;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // Otherwise return as-is (6-digit hex or other format)
      return hex;
    },
    borderStyleFromSettings(border, fallbackColor = "#e5e7eb") {
      const b = border || {};
      if (!b.enabled) return {};
      const styleRaw = String(b.style || "solid").toLowerCase();
      const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
      const color = typeof b.color === "string" && b.color ? b.color : fallbackColor;
      const colorCss = this.hexToCssColor(color);
      const wRaw = Number(b.width ?? 1);
      const width = Number.isFinite(wRaw) ? Math.min(20, Math.max(1, Math.round(wRaw))) : 1;
      const sides = b.sides || {};
      const isOn = (k) => sides[k] !== false;
      const css = (on) => (on ? `${width}px ${style} ${colorCss}` : null);
      return {
        borderTop: css(isOn("top")),
        borderRight: css(isOn("right")),
        borderBottom: css(isOn("bottom")),
        borderLeft: css(isOn("left")),
      };
    },
    outlineStyleFromSettings(border, fallbackColor = "#e5e7eb") {
      const b = border || {};
      if (!b.enabled) return {};
      const styleRaw = String(b.style || "solid").toLowerCase();
      const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
      const color = typeof b.color === "string" && b.color ? b.color : fallbackColor;
      const colorCss = this.hexToCssColor(color);
      const wRaw = Number(b.width ?? 1);
      const width = Number.isFinite(wRaw) ? Math.min(20, Math.max(1, Math.round(wRaw))) : 1;
      const css = `${width}px ${style} ${colorCss}`
      return {
        outline: css,
        outlineOffset: "0px",
      };
    },
    rowMarginStyle(row) {
      const s = row?.settings || {};
      const safe = (n) => {
        const x = Number(n);
        return Number.isFinite(x) && x > 0 ? Math.round(x) : 0;
      };
      const mt = safe(s.marginTop);
      const mr = safe(s.marginRight);
      const mb = safe(s.marginBottom);
      const ml = safe(s.marginLeft);
      return {
        paddingTop: mt ? `${mt}px` : null,
        paddingRight: mr ? `${mr}px` : null,
        paddingBottom: mb ? `${mb}px` : null,
        paddingLeft: ml ? `${ml}px` : null,
      };
    },
    rowStyle(row) {
      const s = row.settings || {};
      const pt = Number(s.paddingTop ?? 8);
      const pb = Number(s.paddingBottom ?? 8);
      const pl = Number(s.paddingLeft ?? 8);
      const pr = Number(s.paddingRight ?? 8);
      const bg = this.backgroundCssFromSettings(s);
      const brCss = this.formatBorderRadius(s.borderRadius);
      const bgColor = s.backgroundColor ? this.hexToCssColor(s.backgroundColor) : "transparent";
      return {
        backgroundColor: bgColor,
        backgroundImage: bg.backgroundImage,
        backgroundSize: bg.backgroundSize,
        backgroundPosition: bg.backgroundPosition,
        backgroundRepeat: bg.backgroundRepeat,
        textAlign: s.align || "left",
        paddingTop: `${pt}px`,
        paddingBottom: `${pb}px`,
        paddingLeft: `${pl}px`,
        paddingRight: `${pr}px`,
        ...this.borderStyleFromSettings(s.border),
        borderRadius: brCss,
        overflow: brCss ? "hidden" : null,
      };
    },
    containerOuterStyle(container) {
      const s = container?.settings || {};
      const bg = this.backgroundCssFromSettings(s);
      const bgColor = s.backgroundColor ? this.hexToCssColor(s.backgroundColor) : "transparent";
      return {
        backgroundColor: bgColor,
        backgroundImage: bg.backgroundImage,
        backgroundSize: bg.backgroundSize,
        backgroundPosition: bg.backgroundPosition,
        backgroundRepeat: bg.backgroundRepeat,
      };
    },
    containerPadStyle(container) {
      const s = container?.settings || {};
      const pt = Number(s.paddingTop ?? 0);
      const pr = Number(s.paddingRight ?? 0);
      const pb = Number(s.paddingBottom ?? 0);
      const pl = Number(s.paddingLeft ?? 0);
      return {
        paddingTop: pt ? `${pt}px` : null,
        paddingRight: pr ? `${pr}px` : null,
        paddingBottom: pb ? `${pb}px` : null,
        paddingLeft: pl ? `${pl}px` : null,
      };
    },
    containerContentStyle(container) {
      const s = container?.settings || {};
      const body = this.doc?.body || {};
      const bgRaw = s.contentBackgroundColor || body.contentBackgroundColor || "#ffffff";
      const bg = this.hexToCssColor(bgRaw);
      
      // Content background image support (from container settings or body defaults)
      const contentBgSettings = {
        backgroundMode: s.contentBackgroundMode || body.contentBackgroundMode,
        backgroundImageUrl: s.contentBackgroundImageUrl || body.contentBackgroundImageUrl,
        backgroundSizeMode: s.contentBackgroundSizeMode || body.contentBackgroundSizeMode,
        backgroundSizeUnit: s.contentBackgroundSizeUnit || body.contentBackgroundSizeUnit,
        backgroundSizePx: s.contentBackgroundSizePx || body.contentBackgroundSizePx,
        backgroundSizePct: s.contentBackgroundSizePct || body.contentBackgroundSizePct,
        backgroundRepeat: s.contentBackgroundRepeat || body.contentBackgroundRepeat,
        backgroundPosition: s.contentBackgroundPosition || body.contentBackgroundPosition,
      };
      const contentBgStyles = this.backgroundCssFromSettings(contentBgSettings);
      
      const w = Number(this.doc?.body?.emailWidth ?? this.doc?.body?.width ?? 600) || 600;
      const br = s.contentBorderRadius ?? 0;
      const bw = Number(s.contentBorderWidth ?? 0) || 0;
      const bs = String(s.contentBorderStyle || "none").toLowerCase();
      const bc = s.contentBorderColor || "#e5e7eb";
      const borderEnabled = bs !== "none" && bw > 0;
      const border = borderEnabled
        ? { enabled: true, style: bs, color: bc, width: bw, sides: s.contentBorderSides || { top: true, right: true, bottom: true, left: true } }
        : { enabled: false };
      const brCss = this.formatBorderRadius(br);
      return {
        maxWidth: `${w}px`,
        width: "100%",
        marginLeft: "auto",
        marginRight: "auto",
        backgroundColor: bg,
        backgroundImage: contentBgStyles.backgroundImage,
        backgroundSize: contentBgStyles.backgroundSize,
        backgroundPosition: contentBgStyles.backgroundPosition,
        backgroundRepeat: contentBgStyles.backgroundRepeat,
        borderRadius: brCss,
        overflow: brCss ? "hidden" : null,
        ...this.borderStyleFromSettings(border, bc),
      };
    },
    blockMarginStyle(block) {
      const s = block?.settings || {};
      const mt = Number.isFinite(Number(s.marginTop)) ? Number(s.marginTop) : 8;
      const mr = Number.isFinite(Number(s.marginRight)) ? Number(s.marginRight) : 0;
      const mb = Number.isFinite(Number(s.marginBottom)) ? Number(s.marginBottom) : 0;
      const ml = Number.isFinite(Number(s.marginLeft)) ? Number(s.marginLeft) : 0;
      return {
        padding: `${mt}px ${mr}px ${mb}px ${ml}px`,
      };
    },
    columnPaddingStyle(row) {
      const s = row?.settings || {};
      return {
        paddingTop: `${Number(s.paddingTop ?? 8)}px`,
        paddingBottom: `${Number(s.paddingBottom ?? 8)}px`,
        paddingLeft: `${Number(s.paddingLeft ?? 8)}px`,
        paddingRight: `${Number(s.paddingRight ?? 8)}px`,
      };
    },
    columnTdStyle(row, colIdx, colCount, col = null) {
      const s = row?.settings || {};
      const cs = col?.settings || {};
      const va = this.columnValign(col);
      const cpt = Number(cs.paddingTop ?? 8);
      const cpb = Number(cs.paddingBottom ?? 8);
      const cpl = Number(cs.paddingLeft ?? 8);
      const cpr = Number(cs.paddingRight ?? 8);
      const bgRaw = cs.backgroundColor || null;
      const bg = bgRaw ? this.hexToCssColor(bgRaw) : null;
      const mode = String(cs.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
      const img = cs.backgroundImageUrl || null;
      const isImage = mode === "image" && img;
      const bgCss = this.backgroundCssFromSettings(cs);
      const brCss = this.formatBorderRadius(cs.borderRadius);

      return {
        textAlign: cs.align || s.align || "left",
        verticalAlign: va,
        paddingTop: `${Number.isFinite(cpt) ? cpt : 0}px`,
        paddingBottom: `${Number.isFinite(cpb) ? cpb : 0}px`,
        paddingLeft: `${Number.isFinite(cpl) ? cpl : 0}px`,
        paddingRight: `${Number.isFinite(cpr) ? cpr : 0}px`,
        backgroundColor: bg,
        backgroundImage: bgCss.backgroundImage,
        backgroundSize: bgCss.backgroundSize,
        backgroundPosition: bgCss.backgroundPosition,
        backgroundRepeat: bgCss.backgroundRepeat,
        ...this.outlineStyleFromSettings(cs.border),
        borderRadius: brCss,
      };
    },
    columnInnerStyle(col) {
      const v = String(col?.settings?.verticalAlign || "top").toLowerCase();
      const va = v === "middle" || v === "bottom" ? v : "top";
      return {
        width: "100%",
        boxSizing: "border-box",
        // Used by inline blocks (image/text inline mode) so their own vertical-align
        // doesn't fight the column vertical-align.
        "--kv-ve-col-vertical-align": va,
      };
    },
    getColumnsWithGaps(row) {
      const cols = row?.columns || [];
      const gap = Number(row?.settings?.columnGap ?? 0);
      if (cols.length <= 1 || gap <= 0) {
        return cols.map((col, idx) => ({ type: "column", col, idx }));
      }
      const result = [];
      for (let i = 0; i < cols.length; i += 1) {
        if (i > 0) {
          result.push({ type: "gap", width: gap });
        }
        result.push({ type: "column", col: cols[i], idx: i });
      }
      return result;
    },
    gapColumnStyle(gapWidth) {
      return {
        width: `${gapWidth}px`,
        minWidth: `${gapWidth}px`,
        maxWidth: `${gapWidth}px`,
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        outline: "none",
      };
    },
    getContainerControlsStyle(container) {
      // Container controls are at top-left, no need for bottom spacing check
      return {};
    },
    getRowControlsStyle(row) {
      // Check distance from bottom of viewport when controls are visible
      // We'll use CSS to handle this, but can add JS calculation if needed
      return {
        maxHeight: "calc(100vh - 120px)",
      };
    },
    getBlockControlsStyle(block) {
      // Check distance from bottom of viewport
      // Minimum 120px from bottom needed for expansion
      // Use CSS calc to ensure menu doesn't go below 120px from bottom
      return {
        maxHeight: "calc(100vh - 120px)",
      };
    },
    getColControlsStyle(col) {
      void col;
      return {
        maxHeight: "calc(100vh - 120px)",
      };
    },
    getDomElFromRef(ref) {
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },
    applyDragMenuBottomGuard(menuEl, { minBottomSpace = 10, minTopSpace = 8 } = {}) {
      if (!menuEl || typeof menuEl.getBoundingClientRect !== "function") return;

      // Predict the expanded menu bottom without forcing expansion.
      // Even when height is collapsed, scrollHeight reflects the full content height.
      const rect = menuEl.getBoundingClientRect();
      const fullHeight = Number(menuEl.scrollHeight) || rect.height || 0;
      const predictedBottom = rect.top + fullHeight;
      const viewportHeight = window.innerHeight || 0;
      const spaceBelow = viewportHeight - predictedBottom;

      let shiftUp = 0;
      if (spaceBelow < minBottomSpace) {
        shiftUp = minBottomSpace - spaceBelow;
        // Prevent shifting past the top edge.
        const maxShift = Math.max(0, rect.top - minTopSpace);
        shiftUp = Math.min(shiftUp, maxShift);
      }

      menuEl.style.setProperty("--kv-ve-menu-shift-y", shiftUp ? `-${Math.round(shiftUp)}px` : "0px");
    },
    applyDragMenuBottomGuardByRef(refKey, opts) {
      const el = this.getDomElFromRef(this.$refs?.[refKey]);
      if (!el) return;
      this.applyDragMenuBottomGuard(el, opts);
    },
    formatBorderRadius(value) {
      if (!value) return null;
      // If it's an object with individual corner values
      if (typeof value === "object" && !Array.isArray(value)) {
        const topLeft = Number(value.topLeft) || 0;
        const topRight = Number(value.topRight) || 0;
        const bottomLeft = Number(value.bottomLeft) || 0;
        const bottomRight = Number(value.bottomRight) || 0;
        return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
      }
      // If it's a number, use uniform radius
      const n = Number(value);
      return Number.isFinite(n) && n >= 0 ? `${n}px` : null;
    },
    columnValign(col) {
      const v = String(col?.settings?.verticalAlign || "top").toLowerCase();
      return v === "middle" || v === "bottom" ? v : "top";
    },
    clonePaletteBlock(item) {
      // Called by vuedraggable when dragging from palette into a column.
      const type = item.type;
      if (type === "text") {
        return createBlock("text", {
          content: {
            tiptapJson: {
              type: "doc",
              content: [{ type: "paragraph", content: [{ type: "text", text: "Seu texto aqui..." }] }],
            },
          },
        });
      }
      if (type === "image") {
        return createBlock("image", {
          src: "",
          alt: this.$_("visualEditor-emailEditor-defaults-imageAlt"),
          href: "",
          borderRadius: 0,
          objectFit: "cover",
          aspectRatio: "auto",
        });
      }
      if (type === "button") {
        return createBlock("button", {
          text: this.$_("visualEditor-emailEditor-add-showMore"),
          href: "https://example.com",
          borderRadius: 8,
          paddingX: 18,
          paddingY: 12,
        });
      }
      if (type === "divider") {
        return createBlock("divider", { color: "#e5e7eb", thickness: 1 });
      }
      if (type === "spacer") {
        return createBlock("spacer", { height: 16 });
      }
      return createBlock("text", {
        content: {
          tiptapJson: {
            type: "doc",
            content: [
              {
                type: "paragraph",
                content: [
                  {
                    type: "text",
                    text: this.$_("visualEditor-emailEditor-defaults-textContent"),
                  },
                ],
              },
            ],
          },
        },
      });
    },
    addCustomRow(columns) {
      const row = createRow({ kind: "custom", columns: Number(columns) || 1 });
      // New rows should start empty (all columns without blocks).
      const c = this.getActiveContainer();
      if (!c) return;
      if (!Array.isArray(c.rows)) this.$set(c, "rows", []);
      c.rows.push(row);
      this.selectRow(row, c);
    },
    addContainer() {
      this.ensureContainers();
      const c = createContainer({ title: "Container", rows: [] });
      this.ensureContainerHasAtLeastOneRow(c);
      this.doc.containers.push(c);
      this.selectContainer(c);
    },
    insertContainerBefore(container) {
      this.insertContainerRelative(container, "before");
    },
    insertContainerAfter(container) {
      this.insertContainerRelative(container, "after");
    },
    insertContainerRelative(container, where = "after") {
      this.ensureContainers();
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      const idx = containers.findIndex((x) => x?.id && x.id === container?.id);
      if (idx < 0) {
        this.addContainer();
        return;
      }
      const next = createContainer({ title: "Container", rows: [] });
      this.ensureContainerHasAtLeastOneRow(next);
      const insertIdx = where === "before" ? idx : idx + 1;
      containers.splice(insertIdx, 0, next);
      this.selectContainer(next);
    },
    duplicateContainer(container) {
      if (!container?.id) return;
      this.ensureContainers();
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      const idx = containers.findIndex((x) => x?.id === container.id);
      if (idx < 0) return;
      const copy = deepClone(container);
      copy.id = kvVeId("container");
      copy.title = copy.title || "Container";
      copy.settings = deepClone(copy.settings || {});
      copy.rows = (copy.rows || []).map((row) => {
        const r = deepClone(row);
        r.id = kvVeId("row");
        r.__isDragClone = false;
        r.__dragCloneOf = null;
        r.columns = (r.columns || []).map((c) => ({
          ...deepClone(c),
          id: kvVeId("col"),
          blocks: (c.blocks || []).map((b) => ({ ...deepClone(b), id: kvVeId("block") })),
        }));
        this.normalizeRowWeights(r);
        return r;
      });
      this.ensureContainerHasAtLeastOneRow(copy);
      containers.splice(idx + 1, 0, copy);
      this.selectContainer(copy);
    },
    deleteContainer(container) {
      if (!container?.id) return;
      // Ensure selection points to the same container, so deleteSelectedContainer behaves correctly.
      this.selectedContainerId = container.id;
      this.deleteSelectedContainer();
    },
    deleteSelectedContainer() {
      const c = this.selectedContainer;
      if (!c?.id) return;
      const containers = Array.isArray(this.doc?.containers) ? this.doc.containers : [];
      if (containers.length <= 1) {
        // keep at least one container
        c.rows = [];
        c.settings = { ...(c.settings || {}), backgroundColor: null, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 };
        c.title = "Body";
        this.ensureContainerHasAtLeastOneRow(c);
        this.clearSelection();
        return;
      }
      this.doc.containers = containers.filter((x) => x.id !== c.id);
      this.clearSelection();
    },
    addSection(section) {
      const row = section.build();
      // Ensure unique ids in case build() returns deterministic objects in future.
      row.id = kvVeId("row");
      row.columns = (row.columns || []).map((c) => ({
        ...c,
        id: kvVeId("col"),
        blocks: (c.blocks || []).map((b) => ({ ...b, id: kvVeId("block") })),
      }));
      const c = this.getActiveContainer();
      if (!c) return;
      if (!Array.isArray(c.rows)) this.$set(c, "rows", []);
      c.rows.push(row);
      this.selectRow(row, c);
    },
    setRowColumns(row, nextCount, nextWeights = null) {
      if (!row) return;
      if (!row.columns) this.$set(row, "columns", []);

      const n = Math.max(1, Math.min(4, Number(nextCount) || 1));
      const current = row.columns?.length || 0;

      // If the row was a preset section, changing columns means it's now a custom row.
      // (Keeps the model honest and avoids "section" rows drifting away from their preset definition.)
      if (row.kind === "section") {
        this.$set(row, "kind", "custom");
        this.$set(row, "preset", null);
      }

      if (n > current) {
        for (let i = current; i < n; i += 1) {
          row.columns.push({ id: kvVeId("col"), blocks: [] });
        }
      } else if (n < current) {
        // Move blocks from removed columns into the last kept column to avoid data loss.
        const kept = row.columns.slice(0, n);
        const last = kept[kept.length - 1];
        const removed = row.columns.slice(n);
        removed.forEach((c) => {
          (c.blocks || []).forEach((b) => last.blocks.push(b));
        });
        row.columns = kept;
      }

      // Apply weights (ratios)
      if (!row.settings) this.$set(row, "settings", {});
      const weights = Array.isArray(nextWeights)
        ? nextWeights
        : Array.isArray(row.settings.columnWeights)
          ? row.settings.columnWeights
          : null;
      const normalized = Array.from({ length: n }, (_, i) => {
        const v = Number(weights?.[i] ?? 1);
        return Number.isFinite(v) && v > 0 ? v : 1;
      });
      this.$set(row.settings, "columnWeights", normalized);
    },
    deleteBlock(row, col, block) {
      col.blocks = (col.blocks || []).filter((b) => b.id !== block.id);
      if (this.selectedBlockId === block.id) {
        this.selectedBlockId = null;
        this.selectedColId = null;
      }
      this.selectedRowId = row.id;
    },
    duplicateBlock(row, col, block) {
      const idx = (col.blocks || []).findIndex((b) => b.id === block.id);
      if (idx < 0) return;
      const copy = deepClone(block);
      copy.id = kvVeId("block");
      col.blocks.splice(idx + 1, 0, copy);
      this.selectBlock(row, col, copy);
    },
    deleteSelectedBlock() {
      if (!this.selectedBlockId) return;
      for (const row of this.allRows) {
        for (const col of row.columns || []) {
          const block = (col.blocks || []).find((b) => b.id === this.selectedBlockId);
          if (block) {
            this.deleteBlock(row, col, block);
            return;
          }
        }
      }
    },
    deleteSelectedCol() {
      if (!this.selectedColId) return;
      const colId = this.selectedColId;
      const row =
        this.selectedRow ||
        (this.allRows || []).find((r) => (r?.columns || []).some((c) => c?.id === colId)) ||
        null;
      if (!row) return;
      const cols = Array.isArray(row.columns) ? row.columns : [];
      if (cols.length <= 1) return; // keep at least one column

      const idx = cols.findIndex((c) => c?.id === colId);
      if (idx < 0) return;

      const removed = cols[idx];
      const targetIdx = idx > 0 ? idx - 1 : 1;
      const target = cols[targetIdx];
      if (!target) return;

      if (!Array.isArray(target.blocks)) this.$set(target, "blocks", []);
      (removed?.blocks || []).forEach((b) => target.blocks.push(b));

      row.columns = cols.filter((c) => c?.id !== colId);

      if (!row.settings) this.$set(row, "settings", {});
      if (Array.isArray(row.settings.columnWeights)) {
        const nextWeights = row.settings.columnWeights.slice(0, cols.length);
        nextWeights.splice(idx, 1);
        this.$set(row.settings, "columnWeights", nextWeights);
      }
      this.normalizeRowWeights(row);

      // clear selection down to row
      this.selectedBlockId = null;
      this.selectedColId = null;
      this.selectedRowId = row.id;
      this.settingsTab = "row";
    },
    deleteSelectedRow() {
      const row = this.selectedRow;
      if (!row) return;
      this.deleteRow(row);
    },
    deleteRow(row) {
      if (!row?.id) return;
      const c = this.findContainerByRowId(row.id);
      if (!c || !Array.isArray(c.rows)) return;
      c.rows = (c.rows || []).filter((r) => r.id !== row.id);
      this.ensureContainerHasAtLeastOneRow(c);
      if (this.selectedRowId === row.id) this.clearSelection();
    },
    duplicateRow(row) {
      if (!row?.id) return;
      const c = this.findContainerByRowId(row.id);
      if (!c || !Array.isArray(c.rows)) return;
      const idx = (c.rows || []).findIndex((r) => r.id === row.id);
      if (idx < 0) return;
      const copy = deepClone(row);
      copy.id = kvVeId("row");
      copy.__isDragClone = false;
      copy.__dragCloneOf = null;
      copy.columns = (copy.columns || []).map((c) => ({
        ...c,
        id: kvVeId("col"),
        blocks: (c.blocks || []).map((b) => ({ ...b, id: kvVeId("block") })),
      }));
      this.normalizeRowWeights(copy);
      c.rows.splice(idx + 1, 0, copy);
      this.selectRow(copy, c);
    },
    async copyHtml() {
      const html = this.exportedHtml;
      try {
        await navigator.clipboard.writeText(html);
        this.$store?.dispatch?.(
          "notification/success",
          this.$_("visualEditor-emailEditor-notifications-htmlCopied"),
        );
      } catch (e) {
        // Fallback
        window.prompt(this.$_("visualEditor-emailEditor-prompts-copyHtml"), html);
      }
    },
    changeTemplate() {
      // TODO: Implement template change functionality
      this.$store?.dispatch?.(
        "notification/info",
        this.$_("visualEditor-emailEditor-notifications-changeTemplateInDevelopment"),
      );
    },

    cloneRowTemplate(item) {
      const cols = Number(item.columns) || 1;
      const row = createRow({ kind: "custom", columns: cols, settings: { columnWeights: item.weights || null } });
      if (item.weights) row.settings.columnWeights = Array.from(item.weights);
      this.normalizeRowWeights(row);
      // Return the row directly - Vue will make it reactive when added via v-model
      return row;
    },
    cloneSectionTemplate(section) {
      const row = section.build();
      // Ensure unique ids
      row.id = kvVeId("row");
      row.columns = (row.columns || []).map((c) => ({
        ...c,
        id: kvVeId("col"),
        blocks: (c.blocks || []).map((b) => ({ ...b, id: kvVeId("block") })),
      }));
      this.normalizeRowWeights(row);
      // Return the row directly - Vue will make it reactive when added via v-model
      return row;
    },
    cloneModule(module) {
      const raw = module?.content;
      const parsed = typeof raw === "string" ? this.safeJsonParse(raw) : raw;
      const row = parsed?.row || null;
      if (!row) {
        this.$store?.dispatch?.(
          "notification/error",
          this.$_("visualEditor-emailEditor-notifications-invalidModuleNoRow"),
        );
        return null;
      }
      return this.rekeyRowForInsert(row);
    },
    onRowsChanged(container, evt) {
      // When a row template/module is dropped (or moved), auto-select it.
      const row = evt?.added?.element || evt?.moved?.element || null;
      if (row?.id) this.selectRow(row, container);
      this.ensureContainerHasAtLeastOneRow(container);
    },

    async submitAiEdit() {
      const prompt = String(this.aiPrompt || "").trim();
      if (!prompt) return;
      if (this.aiLoading) return;

      this.aiLoading = true;
      try {
        this.$store?.dispatch?.(
          "notification/info",
          this.$_("visualEditor-emailEditor-notifications-applyingAi"),
        );

        const payload = {
          prompt,
          doc: deepClone(this.doc),
        };
        const options = keviApi.getOptions("kveditor/ai-edit", "POST", payload);
        const res = await this.$http(options);
        const body = res?.body || res?.data || res || {};
        const nextDoc = body?.doc || body?.data?.doc || null;

        if (!nextDoc) {
          throw new Error("no-doc-returned");
        }

        this.doc = deepClone(nextDoc);
        this.ensureContainers();
        this.clearSelection();
        this.aiPrompt = "";
        this.aiPromptHeight = 150;
        this.$store?.dispatch?.(
          "notification/success",
          this.$_("visualEditor-emailEditor-notifications-emailUpdated"),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        const msg =
          e?.body?.message ||
          e?.data?.message ||
          e?.message ||
          this.$_("visualEditor-emailEditor-notifications-aiApplyError");
        this.$store?.dispatch?.("notification/error", msg);
      } finally {
        this.aiLoading = false;
      }
    },

    onAiPromptInput() {
      this.$nextTick(() => this.resizeAiPromptTextarea());
    },

    resizeAiPromptTextarea() {
      const el = this.$refs.aiPromptTextarea;
      if (!el) return;
      const t = String(this.aiPrompt || "");
      if (!t.trim()) {
        this.aiPromptHeight = 150;
        return;
      }
      // Temporarily shrink to measure content height accurately
      el.style.height = "0px";
      const next = Math.min(500, Math.max(150, el.scrollHeight || 150));
      this.aiPromptHeight = next;
    },

    startAiLoaderSentences() {
      this.stopAiLoaderSentences();
      this.aiLoadingSentenceIdx = 0;
      const list = Array.isArray(this.aiLoadingSentences) ? this.aiLoadingSentences : [];
      if (list.length <= 1) return;
      this.aiLoadingTimer = window.setInterval(() => {
        this.aiLoadingSentenceIdx = (this.aiLoadingSentenceIdx + 1) % list.length;
      }, 2800);
    },

    stopAiLoaderSentences() {
      if (this.aiLoadingTimer) {
        window.clearInterval(this.aiLoadingTimer);
        this.aiLoadingTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve {
  display: grid;
  grid-template-columns: 300px 1fr 300px;
  height: 100vh;
  min-height: 0;
  border: 0;
  border-radius: 0;
  overflow: hidden;
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
}

.kv-ve__sidebar {
  background: #fff;
  display: flex;
  flex-direction: column;
  min-width: 300px;
  min-height: 0;
  overflow: hidden;
}

.kv-ve__sidebar--add {
  border-right: 1px solid #e5e7eb;
}

.kv-ve__sidebar--settings {
  border-left: 1px solid #e5e7eb;
}

/* Responsive: on smaller screens, settings overlays the LEFT sidebar when open */
@media (max-width: 1199px) {
  .kv-ve {
    grid-template-columns: 250px 1fr;
  }
  .kv-ve__sidebar {
    min-width: 250px;
  }
  .kv-ve__sidebar--settings {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0; /* overlay the add sidebar */
    right: auto;
    width: 250px;
    z-index: 4;
    transform: translateX(-100%);
    transition: transform 0.18s ease;
    border-left: 0;
    box-shadow: 0 12px 30px rgba(16, 24, 40, 0.18);
  }
  .kv-ve__sidebar--settings.kv-ve__sidebar--settings-open {
    transform: translateX(0);
  }
  .kv-ve__settings-back {
    display: inline-flex !important;
  }

  /* On small screens, the email design overlay should also live on the left,
     behaving like the settings overlay so all sidebars stack consistently. */
  .kv-ve__email-design-overlay {
    left: 0;
    right: auto;
    width: 250px;
    border-left: 0;
    box-shadow: 0 12px 30px rgba(16, 24, 40, 0.18);
  }
}

/* Mobile UI (sidebars hidden): make main/email area truly full width */
@media (max-width: 768px) {
  .kv-ve.kv-ve--mobile-ui {
    grid-template-columns: 1fr;
  }
  .kv-ve.kv-ve--mobile-ui .kv-ve__sidebar {
    min-width: 0;
  }

  /* Mobile style sidebars: bottom sheet (50vh / 100vw) for BOTH:
     - element settings (block/column/row/section) sidebar
     - global email design settings overlay
  */
  .kv-ve.kv-ve--mobile-ui .kv-ve__sidebar--settings {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100vw;
    height: 50vh;
    max-width: none;
    z-index: 5; /* above mobile insert bar */
    transform: translateY(100%);
    transition: transform 0.22s ease;
    border-left: 0;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 12px 30px rgba(16, 24, 40, 0.18);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
  }
  .kv-ve.kv-ve--mobile-ui .kv-ve__sidebar--settings.kv-ve__sidebar--settings-open {
    transform: translateY(0);
  }
  .kv-ve.kv-ve--mobile-ui .kv-ve__settings-close {
    all: unset;
    cursor: pointer;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #344054;
    margin-right: 6px;
    flex: 0 0 auto;
  }
  .kv-ve.kv-ve--mobile-ui .kv-ve__settings-close:hover {
    background: #f2f4f7;
  }
  .kv-ve.kv-ve--mobile-ui .kv-ve__settings-close .mdi {
    font-size: 18px;
    line-height: 1;
  }

  .kv-ve.kv-ve--mobile-ui .kv-ve__email-design-overlay {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100vw;
    height: 50vh;
    border-left: 0;
    border-top: 1px solid #e5e7eb;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
  }
}
.kv-ve__tabs {
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}
.kv-ve__sidebar--add .kv-ve__tabs {
  border-bottom: 1px solid #e5e7eb;
}
.kv-ve__tabs :deep(.kv-tabs) {
  border-bottom: 0;
}
.kv-ve__tabs :deep(.kv-tabs-scroll-area) {
  padding: 0;
}
.kv-ve__tabs :deep(.kv-tab) {
  padding: 12px 10px;
  font-weight: 600;
  font-size: 12px;
}
.kv-ve__tabs :deep(.kv-tab:hover) {
  background: #f2f4f7;
}
.kv-ve__tabs :deep(.kv-tab.kv-tab--active) {
  color: #2aaa4c;
  border-bottom-color: #2aaa4c;
}
.kv-ve__sidebar-scroll {
  overflow: auto;
  padding: 0;
  min-height: 0;
}
.kv-ve__tabs--settings {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fff;
  border-bottom: 1px solid #eef2f6;
}
.kv-ve__settings-empty {
  padding: 14px 12px;
  color: #667085;
  font-size: 13px;
}

.kv-ve__settings-back {
  all: unset;
  display: none;
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  color: #344054;
  margin-right: 6px;
  flex: 0 0 auto;
}
.kv-ve__settings-back:hover {
  background: #f2f4f7;
}
.kv-ve__settings-back .mdi {
  font-size: 18px;
  line-height: 1;
}

.kv-ve__preview-back-icon {
  all: unset;
  display: none; /* desktop-only */
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  color: #344054;
  margin-right: 6px;
  flex: 0 0 auto;
}
.kv-ve__preview-back-icon:hover {
  background: #f2f4f7;
}
.kv-ve__preview-back-icon .mdi {
  font-size: 18px;
  line-height: 1;
}

@media (min-width: 1200px) {
  /* On desktop, hide the big "Voltar ao editor" button, keep icon-only back */
  .kv-ve--preview .kv-ve__preview-toggle-btn {
    display: none !important;
  }
  .kv-ve--preview .kv-ve__preview-back-icon {
    display: inline-flex !important;
  }
}

/* Settings tab: make settings panels match modern Kevi UI without touching canvas/layout parity */
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__group) {
  margin-bottom: 16px;
  padding: 14px 12px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__group-title) {
  margin-bottom: 12px;
}
.kv-ve__add-group-label {
  margin: 6px 0 10px;
}
.kv-ve__layout-actions {
  padding: 12px 12px 8px;
  border-bottom: 1px solid #eef2f6;
}
.kv-ve__layout-actions :deep(.kv-button) {
  width: 100%;
}
.kv-ve__sidebar-scroll--settings :deep(.form-group) {
  margin-bottom: 12px;
}
.kv-ve__sidebar-scroll--settings :deep(label) {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.kv-ve__sidebar-scroll--settings :deep(.form-control) {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  font-size: 13px;
  padding: 10px 12px;
  height: auto;
  box-shadow: none;
}
.kv-ve__sidebar-scroll--settings :deep(.form-control:focus) {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-trigger) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-trigger-left) {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-trigger-suffix) {
  color: #667085;
  font-weight: 600;
  font-size: 12px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-trigger-caret) {
  color: #667085;
  font-size: 12px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-options) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-option) {
  all: unset;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-option:hover) {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-option.active) {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-preview) {
  width: 100%;
  height: 34px;
  display: grid;
  grid-template-columns: repeat(var(--kv-ve-cols), 1fr);
  gap: 6px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-col) {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__columns-label) {
  font-weight: 700;
  font-size: 12px;
  color: #344054;
}
.kv-ve__sidebar-scroll--settings :deep(.form-text.text-muted),
.kv-ve__sidebar-scroll--settings :deep(.text-muted) {
  color: #667085 !important;
  font-size: 12px;
}
.kv-ve__sidebar-scroll--settings :deep(.btn) {
  border-radius: 12px;
  font-weight: 600;
  padding: 8px 12px;
}
.kv-ve__sidebar-scroll--settings :deep(.btn.btn-danger) {
  background: #fff;
  color: #b42318;
  border: 1px solid #fecdca;
}
.kv-ve__sidebar-scroll--settings :deep(.btn.btn-danger:hover) {
  background: #fffbfa;
}
.kv-ve__group {
  padding: 8px 8px 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  margin-bottom: 8px;
}
.kv-ve__group-title {
  /* typography comes from KvTypography; keep only layout */
  margin-bottom: 10px;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__subsection) {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eef2f6;
}
.kv-ve__sidebar-scroll--settings :deep(.kv-ve__subsection-title) {
  font-weight: 500;
  font-size: 13px;
  color: #727272;
  text-transform: none;
  letter-spacing: 0.01em;
  margin-bottom: 10px;
}
.kv-ve__palette {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.kv-ve__palette--rows {
  grid-template-columns: 1fr;
}
.kv-ve__palette--sections {
  /* Use a simple vertical stack so section cards always take full sidebar width */
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-items: initial;
}
.kv-ve__palette-item--wide {
  grid-column: 1 / -1;
}
.kv-ve__palette-item {
  border: 1px solid #eaecf0;
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  cursor: grab;
  user-select: none;
            user-select: none;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.kv-ve__palette-item--module {
  padding: 0;
  border: none;
}
.kv-ve__palette-item:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 2px 10px rgba(16, 24, 40, 0.10);
}
.kv-ve__palette-item:active {
  transform: translateY(1px);
}
.kv-ve__palette-item--tile {
  height: 112px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}
.kv-ve__palette-item--row {
  align-items: stretch;
  justify-content: flex-start;
  height: auto;
  gap: 10px;
}
.kv-ve__palette-label {
  font-weight: 600;
  font-size: 13px;
  color: #101828;
  line-height: 1.15;
}
.kv-ve__palette-label--center {
  text-align: center;
}
.kv-ve__palette-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  color: #344054;
}
.kv-ve__palette-tile-icon--text { background: rgba(46, 144, 250, 0.10); color: #175cd3; }
.kv-ve__palette-tile-icon--image { background: rgba(247, 144, 9, 0.12); color: #b54708; }
.kv-ve__palette-tile-icon--button { background: rgba(18, 183, 106, 0.12); color: #027a48; }
.kv-ve__palette-tile-icon--divider { background: rgba(102, 112, 133, 0.12); color: #344054; }
.kv-ve__palette-tile-icon--spacer { background: rgba(155, 81, 224, 0.12); color: #7f56d9; }

.kv-ve__palette-row-preview {
  width: 46px;
  height: 34px;
  display: grid;
  grid-template-columns: repeat(var(--kv-ve-cols), 1fr);
  gap: 4px;
  align-items: stretch;
}
.kv-ve__palette-row-preview--wide {
  width: 100%;
  height: 46px;
  gap: 8px;
}
.kv-ve__palette-row-caption {
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  color: #344054;
  margin-top: 2px;
}
.kv-ve__show-more {
  width: 100%;
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
}
.kv-ve__show-more:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.10);
}
.kv-ve__palette-row-col {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.kv-ve__palette-item--section {
  align-items: stretch;
  justify-content: flex-start;
  height: auto;
  max-width: none;
  margin: 0;
  align-self: stretch;
}
.kv-ve__palette-item--section .kv-ve__palette-label {
  padding-left: 2px;
  margin-bottom: 2px;
}

/* Drop placeholder INSIDE the email:
   show only an insertion line indicator. */
.kv-ve-ghost {
  position: relative;
  padding: 0 !important;
  margin: 0 !important;
  height: 3px !important;
  min-height: 3px !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  overflow: hidden !important;
}

.kv-ve-drag {
  visibility: hidden !important;
  opacity: 0 !important;
  background: #ff0;
}

.kv-ve-ghost::before {
  content: "";
  position: absolute;
  left: 9px;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  background:
    radial-gradient(circle at left center, rgba(42, 170, 76, 0.98) 0 3px, transparent 3px),
    radial-gradient(circle at right center, rgba(42, 170, 76, 0.98) 0 3px, transparent 3px),
    linear-gradient(rgba(42, 170, 76, 0.92), rgba(42, 170, 76, 0.92));
  background-repeat: no-repeat;
  background-size: 6px 6px, 6px 6px, 100% 2px;
  background-position: left center, right center, center;
  /* Don't round this layer; rounding clips the circle gradients */
  border-radius: 0;
}

.kv-ve__row-drop-marker {
  pointer-events: none;
}

/* Keep the original row visible while dragging (Sortable fallback) */
.kv-ve__row-chosen {
  opacity: 1 !important;
  display: block !important;
}

/* Cursor-following ghost: small tile with an icon */
.kv-ve__row-dragging {
  width: 44px !important;
  height: 44px !important;
  border-radius: 12px !important;
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.18) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
}
.kv-ve__row-dragging > * {
  display: none !important;
}
.kv-ve__row-dragging::before {
  content: "▦";
  font-size: 18px;
  color: #344054;
  line-height: 1;
}

/* Drag clones are only a temporary visual placeholder. */
.kv-ve__row--clone {
  pointer-events: none;
  position: relative;
}
.kv-ve__row--clone .kv-ve__row-drag {
  visibility: hidden;
}
.kv-ve__row--clone::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 2px solid rgba(42, 170, 76, 0.95);
  border-radius: 0;
  pointer-events: none;
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.12);
}
.kv-ve__section-preview {
  width: 100%;
  /* Let each section preview size naturally (no enforced equal heights) */
  height: auto;
  border-radius: 0;
  background: transparent;
  border: 0;
  overflow: visible;
  display: block;
  padding: 0;
}
.kv-ve__section-preview--product_2 {
  padding-top: 16px;
  padding-bottom: 16px;
}
.kv-ve__section-preview--product_2 .kv-ve__section-wire-card {
  gap: 12px;
}
.kv-ve__section-preview--product_2 .kv-ve__wire-line {
  opacity: 0.9;
}
.kv-ve__section-preview--product_2 .kv-ve__wire-pill--left {
  margin-top: 2px;
}
.kv-ve__section-preview-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
}
.kv-ve__module-preview {
  width: 100%;
  height: auto;
  min-height: 0;
  border-radius: 8px;
  background: #f9fafb;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
}
.kv-ve__module-edit-btn {
  all: unset;
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(234, 236, 240, 0.95);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.10);
  z-index: 2;
}
.kv-ve__module-edit-btn:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.10), 0 8px 20px rgba(16, 24, 40, 0.10);
}
.kv-ve__module-edit-btn .mdi {
  font-size: 16px;
  line-height: 1;
}
.kv-ve__module-preview-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}
.kv-ve__module-preview-fallback {
  width: 100%;
  padding: 20px;
  text-align: center;
  color: #667085;
  font-size: 12px;
}
.kv-ve__modules-loading,
.kv-ve__modules-empty {
  padding: 20px;
  text-align: center;
  color: #667085;
  font-size: 13px;
}
.kv-ve__section-wire {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}
.kv-ve__wire-line {
  height: 7px;
  background: #e5e7eb;
  border-radius: 99px;
}
.kv-ve__wire-line--w70 { width: 70%; }
.kv-ve__wire-line--w75 { width: 75%; }
.kv-ve__wire-line--w85 { width: 85%; }
.kv-ve__wire-line--w90 { width: 90%; }
.kv-ve__wire-line--w92 { width: 92%; }
.kv-ve__wire-line--center { margin-left: auto; margin-right: auto; }
.kv-ve__wire-pill {
  width: 56%;
  height: 22px;
  border-radius: 999px;
  background: #111827;
  opacity: 0.85;
  margin-left: auto;
  margin-right: auto;
}
.kv-ve__section-wire-grid {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 10px;
  align-items: center;
}
.kv-ve__section-wire-grid--text-image {
  grid-template-columns: 1.1fr 0.9fr;
}
.kv-ve__section-wire-grid--3 {
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.kv-ve__section-wire-grid--2 {
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.kv-ve__section-wire-box {
  width: 100%;
  height: 56px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background:
    linear-gradient(135deg, rgba(46, 144, 250, 0.10), rgba(18, 183, 106, 0.06)),
    #f9fafb;
}
.kv-ve__section-wire-box--tall {
  height: 44px;
}
.kv-ve__section-wire-box--image-divider {
  height: 38px;
  border-radius: 0;
}
.kv-ve__section-wire-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kv-ve__section-wire-mini {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}
.kv-ve__section-wire-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kv-ve__wire-pill--left {
  margin-left: 0;
  margin-right: auto;
  width: 66%;
}
.kv-ve__wire-divider {
  width: 100%;
  height: 2px;
  border-radius: 99px;
  background: #e5e7eb;
  opacity: 0.95;
}
.kv-ve__section-wire-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.kv-ve__wire-icon {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #111827;
  opacity: 0.85;
  background: transparent;
}
.kv-ve__hint {
  margin-top: 10px;
  font-size: 12px;
  color: #667085;
}
.kv-ve__row-buttons {
  display: flex;
  gap: 8px;
}
.kv-ve__section-btn {
  width: 100%;
  text-align: left;
  margin-bottom: 8px;
}

.kv-ve__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
}
.kv-ve__topbar {
  height: 45px; /* match sidebar tabs height */
  padding: 0 12px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  position: relative;
  z-index: 100;
}
.kv-ve__topbar-left,
.kv-ve__topbar-center,
.kv-ve__topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.kv-ve__topbar-center {
  flex: 1;
  justify-content: center;
}
.kv-ve__device-toggle {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 10px;
  overflow: visible;
  gap: 6px;
}
.kv-ve__device-btn {
  all: unset;
  cursor: pointer;
  width: 34px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  background: transparent;
  border-right: 0;
  user-select: none;
  font-size: 16px;
  line-height: 1;
  border-radius: 10px;
}
.kv-ve__device-btn:last-child {
  border-right: 0;
}
.kv-ve__device-btn:hover {
  background: rgba(42, 170, 76, 0.10);
  color: #027a48;
}
.kv-ve__device-btn.active {
  background: rgba(42, 170, 76, 0.14);
  color: #027a48;
}
.kv-ve__topbar-icon-btn {
  height: 32px;
  width: 32px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #667085;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.12s ease, box-shadow 0.12s ease, color 0.12s ease;
}
.kv-ve__topbar-icon-btn:hover:not(:disabled) {
  background: rgba(42, 170, 76, 0.10);
  color: #027a48;
}
.kv-ve__topbar-icon-btn:active:not(:disabled) {
  background: rgba(42, 170, 76, 0.16);
  color: #027a48;
}
.kv-ve__topbar-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.kv-ve__topbar-icon-btn.active {
  background: rgba(42, 170, 76, 0.14);
  color: #027a48;
}
.kv-ve__topbar-icon-btn .mdi {
  font-size: 18px;
  line-height: 1;
}
.kv-ve__topbar-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}

.kv-ve__topbar .btn {
  height: 32px;
  border-radius: 10px;
  font-weight: 600;
  border-color: #e5e7eb;
}
.kv-ve__topbar .btn.btn-light {
  background: #ffffff;
}
.kv-ve__topbar .btn.btn-outline-secondary {
  background: #ffffff;
  color: #344054;
}
.kv-ve__topbar :deep(.kv-button) {
  border-radius: 10px;
  height: 32px;
}
.kv-ve__canvas {
  flex: 1;
  overflow: auto;
  padding: 0;
  min-height: 0;
  scrollbar-gutter: stable;
  position: relative;
}

/* Template gallery modal placeholder */
.kv-ve__template-gallery {
  padding: 14px;
}
.kv-ve__template-gallery-title {
  font-weight: 700;
  color: #101828;
  margin-bottom: 8px;
}
.kv-ve__template-gallery-tabs {
  display: inline-flex;
  gap: 8px;
  margin-bottom: 12px;
}
.kv-ve__template-gallery-tab {
  all: unset;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 700;
  font-size: 12px;
  color: #344054;
}
.kv-ve__template-gallery-tab:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.10);
}
.kv-ve__template-gallery-tab.active {
  border-color: rgba(42, 170, 76, 0.85);
  background: rgba(42, 170, 76, 0.08);
  color: #027a48;
}
.kv-ve__template-gallery-empty {
  color: #667085;
  font-size: 13px;
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  padding: 14px;
  background: #fcfcfd;
}
.kv-ve__template-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.kv-ve__template-card {
  all: unset;
  cursor: pointer;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fff;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  position: relative;
}
.kv-ve__template-card--blank {
  border-style: dashed;
  background: #fcfcfd;
}
.kv-ve__template-card--blank:hover {
  border-style: dashed;
}
.kv-ve__template-card:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 2px 10px rgba(16, 24, 40, 0.10);
}
.kv-ve__template-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14), 0 2px 10px rgba(16, 24, 40, 0.10);
  border-color: rgba(42, 170, 76, 0.75);
}
.kv-ve__template-edit-btn {
  all: unset;
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(234, 236, 240, 0.95);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.10);
}
.kv-ve__template-edit-btn:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.10), 0 8px 20px rgba(16, 24, 40, 0.10);
}
.kv-ve__template-edit-btn .mdi {
  font-size: 16px;
  line-height: 1;
}

/* Save asset modal */
.kv-ve__save-asset-form {
  padding: 6px 0;
}
.kv-ve__save-asset-hint {
  color: #667085;
  font-size: 12px;
  margin-top: 4px;
}
.kv-ve__radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.kv-ve__radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #344054;
}
.kv-ve__radio input {
  margin-top: 3px;
}
.kv-ve__save-asset-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}
.kv-ve__save-asset-footer-spacer {
  flex: 1;
}
.kv-ve__template-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, rgba(46, 144, 250, 0.08), rgba(18, 183, 106, 0.06));
  border-radius: 10px;
  border: 1px solid #eef2f6;
  overflow: hidden;
  display: grid;
  place-items: center;
}
.kv-ve__template-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.kv-ve__template-thumb-fallback {
  font-size: 12px;
  color: #667085;
  font-weight: 600;
}
.kv-ve__template-thumb-fallback--blank {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: #475467;
}
.kv-ve__template-thumb-fallback--blank .mdi {
  font-size: 34px;
  line-height: 1;
}
.kv-ve__template-name {
  font-weight: 400;
  font-size: 12px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kv-ve__template-meta {
  font-size: 12px;
  color: #667085;
}

/* Email design overlay (right sidebar overlay) */
.kv-ve__email-design-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  z-index: 5;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.kv-ve__email-design-header {
  height: 45px;
  padding: 0 12px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.kv-ve__email-design-title {
  font-weight: 400;
  font-size: 13px;
  color: #101828;
}
.kv-ve__email-design-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

/* Apply same field styles as settings sidebar */
.kv-ve__email-design-body :deep(.kv-ve__group) {
  margin-bottom: 16px;
  padding: 14px 12px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.kv-ve__email-design-body :deep(.kv-ve__group-title) {
  margin-bottom: 12px;
}
.kv-ve__email-design-body :deep(.form-group) {
  margin-bottom: 12px;
}
.kv-ve__email-design-body :deep(label) {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.kv-ve__email-design-body :deep(.form-control) {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  font-size: 13px;
  padding: 10px 12px;
  height: auto;
  box-shadow: none;
}
.kv-ve__email-design-body :deep(.form-control:focus) {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}

/* Preview drawer */
.kv-ve__preview-drawer {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Vue transition animations */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.25s ease-out;
}
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
.slide-right-enter-to,
.slide-right-leave {
  transform: translateX(0);
}

/* On small screens, slide-right should animate from the left (left -> right) */
@media (max-width: 1199px) {
  .slide-right-enter,
  .slide-right-leave-to {
    transform: translateX(-100%);
  }
}

/* On mobile, global email settings is a bottom sheet, so slide-right becomes slide-up */
@media (max-width: 768px) {
  .slide-right-enter,
  .slide-right-leave-to {
    transform: translateY(100%);
  }
  .slide-right-enter-to,
  .slide-right-leave {
    transform: translateY(0);
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-out;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
}
.slide-up-enter-to,
.slide-up-leave {
  transform: translateY(0);
}
.kv-ve__preview-drawer-topbar {
  height: 52px;
  padding: 0 12px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.kv-ve__preview-drawer-title {
  font-weight: 500;
  color: #101828;
  font-size: 13px;
}
.kv-ve__preview-drawer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.kv-ve__preview-drawer-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0;
}
.kv-ve__preview-viewport {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  background: #f9fafb;
}
.kv-ve__preview-viewport--desktop {
  padding: 18px 0;
}
.kv-ve__preview-viewport--mobile {
  padding: 18px 0;
}
.kv-ve__iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

/* Add padding when text toolbar is visible */
.kv-ve__email {
  width: 100%;
  margin: 0 auto;
  /* Containers handle their own stripe/content visuals now */
  box-shadow: none;
  border: 0;
  /* Strong isolation from host CSS */
  font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif) !important;
  font-size: var(--kv-ve-font-size, 16px) !important;
  line-height: var(--kv-ve-line-height, 1.5) !important;
  font-weight: 400 !important;
  color: var(--kv-ve-text-color, #111827) !important;
}
.kv-ve__rows {
  padding: 0;
}
.kv-ve__row {
  position: relative;
}
.kv-ve__row.selected {
  z-index: 2;
}
.kv-ve__row.selected::after {
  content: "";
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border: 2px solid rgba(42, 170, 76, 0.95);
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
  pointer-events: none;
  z-index: 6;
}
.kv-ve__row-controls {
  position: absolute;
  /* Place the row handle/tools INSIDE the row */
  z-index: 9999;
  left: 1px;
  right: auto;
  top: 1px;
  width: 32px;
  min-height: 32px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 4px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.10);
  user-select: none;
  pointer-events: auto;
  transition-property: height, opacity, visibility, transform;
  transition-duration: 0.16s, 0.12s, 0s, 0.16s;
  transition-timing-function: ease, ease, linear, ease;
  transition-delay: 0s, 0s, 0s, 0s;
  /* Default: show only drag handle */
  height: 32px;
  transform: translateY(var(--kv-ve-menu-shift-y, 0px));
}

/* Show the handle on selection */
.kv-ve__row.selected .kv-ve__row-controls {
  opacity: 1;
  visibility: visible;
}

/* Expand and reveal actions on hover of controls */
.kv-ve__row-controls:hover,
.kv-ve__row-controls:focus-within {
  /* Calculate height based on number of tools (drag + 3-4 tools) */
  height: auto;
  max-height: calc(100vh - 120px); /* Ensure minimum 120px from bottom */
}

.kv-ve__row-drag {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  font-size: 12px;
  color: #667085;
  user-select: none;
  flex: 0 0 auto;
  position: relative;
}
.kv-ve__row-tool {
  all: unset;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #344054;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, background 0.12s ease, color 0.12s ease;
  flex: 0 0 auto;
}
.kv-ve__row-controls:hover .kv-ve__row-tool,
.kv-ve__row-controls:focus-within .kv-ve__row-tool {
  opacity: 1;
  pointer-events: auto;
}
.kv-ve__row-tool:hover {
  background: #f2f4f7;
}
.kv-ve__row-tool.active {
  background: rgba(42, 170, 76, 0.14);
  color: #027a48;
}
.kv-ve__row-tool--danger {
  color: #b42318;
}
.kv-ve__row-tool .mdi {
  font-size: 18px;
  line-height: 1;
}
.kv-ve__row-margin {
  width: 100%;
  box-sizing: border-box;
}
.kv-ve__row-inner {
  position: relative;
}
.kv-ve__containers {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.kv-ve__container {
  position: relative;
  outline: none;
  border-radius: 0;
  overflow: visible;
  background: transparent;
}
.kv-ve__container.selected::before {
  content: "";
  position: absolute;
  inset: 0;
  outline: 2px solid rgba(42, 170, 76, 0.65);
  outline-offset: 3px;
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.12);
  pointer-events: none;
  z-index: 2;
}
.kv-ve__container-controls {
  position: absolute;
  left: 12px;
  top: 10px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 4px;
  width: 32px;
  min-height: 32px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(234, 236, 240, 0.9);
  border-radius: 10px;
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.08);
  user-select: none;
  overflow: hidden;
  transition-property: height, transform;
  transition-duration: 0.16s;
  transition-timing-function: ease;
  /* Default: show only drag handle */
  height: 32px;
  transform: translateY(var(--kv-ve-menu-shift-y, 0px));
}

/* Expand and reveal actions on hover of controls */
.kv-ve__container-controls:hover,
.kv-ve__container-controls:focus-within {
  height: auto;
  max-height: calc(100vh - 120px); /* Ensure minimum 120px from bottom */
}
.kv-ve__container-insert {
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  z-index: 3;
  pointer-events: none;
}
.kv-ve__container-insert-btn {
  pointer-events: auto;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(234, 236, 240, 0.95);
  background: rgba(255, 255, 255, 0.92);
  color: #344054;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.10);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
}
.kv-ve__container-insert-btn:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12), 0 8px 20px rgba(16, 24, 40, 0.10);
}
.kv-ve__container-insert-tip {
  pointer-events: auto;
  position: absolute;
  right: 0;
}
.kv-ve__container-insert-tip--top {
  top: -16px;
}
.kv-ve__container-insert-tip--bottom {
  bottom: -16px;
}
.kv-ve__container-drag {
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: grab;
  color: #667085;
  user-select: none;
  flex: 0 0 auto;
}
.kv-ve__container-drag:hover {
  background: #f2f4f7;
}
.kv-ve__container-tool {
  all: unset;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #344054;
  flex: 0 0 auto;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, background 0.12s ease;
}
.kv-ve__container-tool--danger {
  color: #b42318;
}
.kv-ve__container-tool--danger .mdi {
  color: #b42318;
}
.kv-ve__container-controls:hover .kv-ve__container-tool,
.kv-ve__container-controls:focus-within .kv-ve__container-tool {
  opacity: 1;
  pointer-events: auto;
}
.kv-ve__container-tool:hover {
  background: #f2f4f7;
}
.kv-ve__container-tool.active {
  background: rgba(42, 170, 76, 0.12);
  color: #027a48;
}
.kv-ve__container-pad {
  padding: 0;
}
.kv-ve__container-content {
  padding: 0;
}
.kv-ve__columns {
  display: flex;
}
.kv-ve__col {
  flex: 1;
  min-width: 0;
  position: relative;
  cursor: pointer;
}
.kv-ve__col-td {
  position: relative;
}

/* Mobile "tap to insert" hotspots (only visible while a mobile insert item is pending) */
.kv-ve__mobile-drop {
  position: absolute;
  inset: 6px;
  z-index: 8;
  border-radius: 12px;
  border: 2px dashed rgba(42, 170, 76, 0.65);
  background: rgba(42, 170, 76, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}
.kv-ve__mobile-drop--move {
  border-color: rgba(59, 130, 246, 0.75);
  background: rgba(59, 130, 246, 0.10);
}
.kv-ve__mobile-drop--move .kv-ve__mobile-drop-inner {
  border-color: rgba(59, 130, 246, 0.35);
  color: #1d4ed8;
}
.kv-ve__mobile-drop-inner {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(42, 170, 76, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 22px rgba(16, 24, 40, 0.14);
  color: #027a48;
}
.kv-ve__mobile-drop-inner .mdi {
  font-size: 18px;
}
.kv-ve__mobile-drop--row {
  inset: 10px;
  z-index: 6; /* above container pad/content, below controls (z=3) still OK due to stacking context */
}

.kv-ve__col-controls {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 4px;
  width: 32px;
  min-height: 32px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.10);
  user-select: none;
  pointer-events: auto;
  transform: translateY(var(--kv-ve-menu-shift-y, 0px));
}

.kv-ve__col-tool {
  all: unset;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #344054;
  transition: background 0.12s ease, color 0.12s ease;
}
.kv-ve__col-tool:hover {
  background: #f2f4f7;
}
.kv-ve__col-tool.active {
  background: rgba(42, 170, 76, 0.14);
  color: #027a48;
}
.kv-ve__col-tool .mdi {
  font-size: 18px;
  line-height: 1;
}
.kv-ve__col-td:not(.kv-ve__col-td--selected):hover::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px dashed rgba(16, 24, 40, 0.25);
  pointer-events: none;
  z-index: 1;
}
.kv-ve__col-td--selected::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid rgba(42, 170, 76, 0.90);
  pointer-events: none;
  z-index: 1;
}
.kv-ve__row-table {
  width: 100%;
  border-collapse: collapse;
}
.kv-ve__gap-col {
  pointer-events: none;
  user-select: none;
}
.kv-ve__row-table td {
  vertical-align: inherit;
}
.kv-ve__col-drag {
  min-height: 5px;
  padding: 0;
}
.kv-ve__drop-empty {
  border: 1px dashed #d0d5dd;
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  color: #667085;
  background: #f9fafb;
  font-size: 13px;
}
.kv-ve__block {
  position: relative;
  border-radius: 12px;
  padding: 0;
  background: transparent;
}
.kv-ve__block-wrap {
  /* Editor "margins" are represented as outer padding to match preview (td padding) exactly. */
  background: transparent;
  position: relative;
  padding-right: 50px; /* Space for block tools on the right */
}
.kv-ve__block-wrap--inline {
  display: inline-block;
  vertical-align: var(--kv-ve-col-vertical-align, top);
}
.kv-ve__block + .kv-ve__block {
  margin-top: 0;
}
.kv-ve__block.selected::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid rgba(42, 170, 76, 0.92);
  border-radius: 0;
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.14);
  pointer-events: none;
}

/* While dragging, nothing should look selected */
.kv-ve--dragging .kv-ve__row.selected {
  outline: none !important;
}
.kv-ve--drag-rows .kv-ve__row-controls {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
.kv-ve--drag-rows .kv-ve__col-drag {
  pointer-events: none;
}
.kv-ve--drag-rows .kv-ve__col-drag .kv-ve__drop-empty {
  display: none;
}
.kv-ve--dragging .kv-ve__block.selected::before {
  display: none !important;
}
.kv-ve--dragging .kv-ve__block-tools {
  display: none !important;
}

/* While dragging, avoid any accidental text selection anywhere inside the editor. */
.kv-ve--dragging,
.kv-ve--dragging * {
  user-select: none !important;
  -webkit-user-select: none !important;
}

    /* While dragging, enlarge droppable areas so it's easier to land inserts. */
    .kv-ve--dragging .kv-ve__col-drag {
      min-height: 25px;
    }
    .kv-ve--dragging .kv-ve__rows {
      min-height: 25px;
    }

/* All blocks are full-bleed; spacing is controlled by row padding + spacer blocks. */
.kv-ve__block-content {
  /* isolate email content from Bootstrap/Vuetify defaults without affecting editor chrome */
  font: inherit;
  color: inherit;
}

.kv-ve__block-content,
.kv-ve__block-content * {
  box-sizing: border-box;
  /* Override global app.css defaults (e.g. * { font-family: ... }) but allow TipTap inline styles */
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

.kv-ve__block-content :is(h1,h2,h3,h4,h5,h6,p,blockquote,pre,ul,ol,li,figure) {
  margin: 0 !important;
  padding: 0 !important;
}

.kv-ve__block-content ul,
.kv-ve__block-content ol {
  padding-left: 18px !important;
}

.kv-ve__block-content img {
  max-width: 100%;
  height: auto;
  display: block;
}

.kv-ve__block-content a {
  color: var(--kv-ve-link-color, inherit);
  text-decoration: underline !important;
}

/* Non-text blocks should never pick up app-wide typography (Montserrat, weights, etc.) */
.kv-ve__block:not(.kv-ve__block--text) .kv-ve__block-content,
.kv-ve__block:not(.kv-ve__block--text) .kv-ve__block-content * {
  font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif) !important;
  font-size: var(--kv-ve-font-size, 16px) !important;
  line-height: var(--kv-ve-line-height, 1.5) !important;
  font-weight: 400 !important;
  color: var(--kv-ve-text-color, #111827) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}

/* Strong typography isolation ONLY for text blocks (prevents Kevi app.css from leaking)
   IMPORTANT: do NOT use !important on font-size/color/font-family for all descendants,
   otherwise TipTap inline styles (color/font size/font family) won't render live. */
.kv-ve__block--text .kv-ve__block-content {
  font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif);
  font-size: var(--kv-ve-font-size, 16px);
  line-height: var(--kv-ve-line-height, 1.5);
  font-weight: 400;
  color: var(--kv-ve-text-color, #111827);
  text-transform: none;
  letter-spacing: normal;
  max-width: 100%;
}

/* Override global app.css like `* { font-family: Montserrat; font-weight: 500; }`
   via specificity (not !important), so TipTap inline styles still win. */
.kv-ve__block--text .kv-ve__block-content * {
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  font-weight: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
}

/* Kevi styles common tags directly (e.g. `h1,h2 { text-transform: uppercase; font-weight:600 }`).
   Force our defaults on block-level tags with !important, but never override TipTap inline styles on spans. */
.kv-ve__block--text .ProseMirror :is(h1,h2,h3,h4,h5,h6,p,li,ul,ol,blockquote,pre) {
  font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif) !important;
  color: var(--kv-ve-text-color, #111827) !important;
  font-weight: 400 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}

/* For inline elements that Kevi might style directly, restore inheritance unless TipTap set an inline style. */
.kv-ve__block--text .ProseMirror span:not([style*="font-family"]) {
  font-family: inherit !important;
}
.kv-ve__block--text .ProseMirror span:not([style*="font-size"]) {
  font-size: inherit !important;
}
.kv-ve__block--text .ProseMirror span:not([style*="color"]) {
  color: inherit !important;
}
.kv-ve__block--text .ProseMirror span:not([style*="font-weight"]) {
  font-weight: inherit !important;
}

.kv-ve__block--text .kv-ve__block-content strong {
  font-weight: 700 !important;
}
.kv-ve__block--text .kv-ve__block-content em {
  font-style: italic !important;
}
.kv-ve__block--text .kv-ve__block-content u {
  text-decoration: underline !important;
}
.kv-ve__block--text .kv-ve__block-content s {
  text-decoration: line-through !important;
}

.kv-ve__block--text .kv-ve__block-content a {
  color: var(--kv-ve-link-color, #0ea5e9) !important;
  text-decoration: underline !important;
}

.kv-ve__block--text .kv-ve__block-content h1 {
  font-size: 32px !important;
  line-height: 1.2 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: inherit !important;
}
.kv-ve__block--text .kv-ve__block-content h2 {
  font-size: 24px !important;
  line-height: 1.2 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: inherit !important;
}
.kv-ve__block--text .kv-ve__block-content :is(h3,h4,h5,h6) {
  font-size: 18px !important;
  line-height: 1.25 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: inherit !important;
}

.kv-ve__block-content table {
  border-collapse: collapse;
}

.kv-ve__block-tools {
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 4px;
  width: 32px;
  min-height: 32px;
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.12);
  z-index: 5;
  overflow: hidden;
  transition-property: height, transform;
  transition-duration: 0.16s;
  transition-timing-function: ease;
  /* Default: show only drag handle */
  height: 32px;
  transform: translateY(var(--kv-ve-menu-shift-y, 0px));
}

/* Expand and reveal actions on hover of controls */
.kv-ve__block-tools:hover,
.kv-ve__block-tools:focus-within {
  height: auto;
  max-height: calc(100vh - 120px); /* Ensure minimum 120px from bottom */
}
.kv-ve__drag-handle {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  cursor: grab;
  user-select: none;
  font-size: 12px;
  line-height: 1;
  flex: 0 0 auto;
}
.kv-ve__tool {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, background 0.12s ease;
  flex: 0 0 auto;
}

/* Mobile: the settings button must be visible even when the menu is "collapsed",
   and the SVG icon must inherit a readable color (avoid white-on-white). */
@media (max-width: 768px) {
  .kv-ve--mobile-ui .kv-ve__tool--settings,
  .kv-ve--mobile-ui .kv-ve__col-tool--settings,
  .kv-ve--mobile-ui .kv-ve__row-tool--settings,
  .kv-ve--mobile-ui .kv-ve__container-tool--settings {
    opacity: 1 !important;
    pointer-events: auto !important;
    color: #344054 !important;
  }
  .kv-ve--mobile-ui .kv-ve__tool--settings :deep(svg),
  .kv-ve--mobile-ui .kv-ve__col-tool--settings :deep(svg),
  .kv-ve--mobile-ui .kv-ve__row-tool--settings :deep(svg),
  .kv-ve--mobile-ui .kv-ve__container-tool--settings :deep(svg) {
    fill: currentColor;
    stroke: currentColor;
  }
}

/* Mobile action menu popup (opened by cog) */
.kv-ve__mobile-action-overlay {
  position: fixed;
  inset: 0;
  z-index: 2950; /* above mobile insert bar + settings sheet */
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.kv-ve__mobile-action-sheet {
  width: 95vw;
  max-width: 520px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 50px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}
.kv-ve__mobile-action-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 10px;
  background: rgba(242, 244, 247, 0.96);
  border-bottom: 1px solid #e5e7eb;
}
.kv-ve__mobile-action-title {
  font-size: 14px;
  font-weight: 700;
  color: #101828;
}
.kv-ve__mobile-action-close {
  all: unset;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #344054;
}
.kv-ve__mobile-action-close:hover {
  background: #f2f4f7;
}
.kv-ve__mobile-action-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.kv-ve__mobile-action-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #101828;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
}
.kv-ve__mobile-action-btn:hover {
  border-color: rgba(42, 170, 76, 0.45);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.10);
}
.kv-ve__mobile-action-btn--danger {
  border-color: rgba(240, 68, 56, 0.22);
  color: #b42318;
}
.kv-ve__mobile-action-btn--danger:hover {
  border-color: rgba(240, 68, 56, 0.35);
  box-shadow: 0 0 0 4px rgba(240, 68, 56, 0.10);
}
.kv-ve__mobile-action-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(16, 24, 40, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #344054;
  flex: 0 0 auto;
}
.kv-ve__block-tools:hover .kv-ve__tool,
.kv-ve__block-tools:focus-within .kv-ve__tool {
  opacity: 1;
  pointer-events: auto;
}
.kv-ve__tool.active {
  background: rgba(42, 170, 76, 0.12);
  border-radius: 8px;
}
.kv-ve__tool .mdi {
  font-size: 18px;
  line-height: 1;
  color: #344054;
}
.kv-ve__tool.danger .mdi {
  color: #b42318;
}
.kv-ve__tool.danger {
  border: 0;
  background: transparent;
}

/* Style paste mode highlights */
.kv-ve--style-paste .kv-ve__style-target {
  outline: 2px dashed rgba(42, 170, 76, 0.75);
  outline-offset: 3px;
  cursor: copy;
}
.kv-ve--style-paste .kv-ve__style-target:hover {
  outline-style: solid;
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.12);
}

/* Rows: make paste target area easier to click by extending the hit box outside the row */
.kv-ve--style-paste .kv-ve__row.kv-ve__style-target {
  position: relative;
  outline: none;
}
.kv-ve--style-paste .kv-ve__row.kv-ve__style-target::before {
  content: "";
  position: absolute;
  inset: -10px; /* expands clickable/hoverable area */
  outline: 2px dashed rgba(42, 170, 76, 0.75);
  outline-offset: 3px;
  border-radius: 12px;
  pointer-events: auto;
}
.kv-ve--style-paste .kv-ve__row.kv-ve__style-target:hover::before {
  outline-style: solid;
  box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.12);
}

/* While pasting ROW styles, ensure clicks land on the row (not on children inside) */
.kv-ve--style-paste-row .kv-ve__row.kv-ve__style-target * {
  pointer-events: none !important;
}

/* While pasting COLUMN styles/duplication, ensure clicks land on the column slot (td) */
.kv-ve--style-paste-col .kv-ve__col-td.kv-ve__style-target * {
  pointer-events: none !important;
}

/* While pasting SECTION/CONTAINER styles, ensure clicks land on the container (not on children inside) */
.kv-ve--style-paste-container .kv-ve__container.kv-ve__style-target * {
  pointer-events: none !important;
}
/* Drop placeholder INSIDE a column while dragging blocks:
   show only an insertion line indicator (same as rows/sections). */
.kv-ve__col-drag .kv-ve__ghost {
  position: relative;
  padding: 0 !important;
  margin: 0 !important;
  height: 3px !important;
  min-height: 3px !important;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  overflow: visible !important;
  opacity: 1 !important;
}
.kv-ve__col-drag .kv-ve__ghost > * {
  display: none !important;
}
.kv-ve__col-drag .kv-ve__ghost::before {
  content: "";
  position: absolute;
  left: 9px;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  background:
    radial-gradient(circle at left center, rgba(42, 170, 76, 0.98) 0 3px, transparent 3px),
    radial-gradient(circle at right center, rgba(42, 170, 76, 0.98) 0 3px, transparent 3px),
    linear-gradient(rgba(42, 170, 76, 0.92), rgba(42, 170, 76, 0.92));
  background-repeat: no-repeat;
  background-size: 6px 6px, 6px 6px, 100% 2px;
  background-position: left center, right center, center;
  /* Don't round this layer; rounding clips the circle gradients */
  border-radius: 0;
}
.kv-ve__preview {
  flex: 1;
  overflow: auto;
  background: #f2f4f7;
  min-height: 0;
  scrollbar-gutter: stable;
  display: flex;
}
.kv-ve__preview-viewport {
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
}
.kv-ve__preview-viewport--desktop {
  align-items: stretch;
}
.kv-ve__preview-viewport--mobile {
  align-items: flex-start;
  padding: 16px;
}
.kv-ve__preview-viewport--mobile .kv-ve__iframe {
  width: 390px;
  max-width: 390px;
  flex: 0 0 auto;
  min-width: 0;
  border-radius: 22px;
  box-shadow: 0 0 0 10px #111827, 0 16px 40px rgba(16, 24, 40, 0.18);
  background: #ffffff;
}
.kv-ve__iframe {
  width: 100%;
  border: none;
  background: #fff;
  display: block;
  min-height: 200px;
  min-width: 0;
  flex: 1 1 auto;
}

/* AI loading overlay */
.kv-ve__ai-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2500; /* above canvas and selection UI, below preview drawer */
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  pointer-events: auto;
}
.kv-ve__ai-loading-card {
  width: calc(100vw - 40px);
  max-width: 420px;
  border-radius: 16px;
  padding: 18px 16px;
  background: #ffffff;
  border: 2px solid transparent;
  background-image:
    linear-gradient(#ffffff, #ffffff),
    linear-gradient(
      83deg,
      #F49E8A 0%,
      #E83E76 25%,
      #936CAD 50%,
      #3FAED5 75%,
      #F49E8A 100%
    );
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 0 20px 60px rgba(16, 24, 40, 0.22);
  display: grid;
  justify-items: center;
  gap: 8px;
  text-align: center;
}
.kv-ve__ai-loading-spinner {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 3px solid rgba(16, 24, 40, 0.10);
  border-top-color: rgba(232, 62, 118, 0.95);
  animation: kvVeSpin 0.9s linear infinite;
}
.kv-ve__ai-loading-title {
  font-weight: 800;
  font-size: 13px;
  color: #101828;
}
.kv-ve__ai-loading-sentence {
  font-weight: 700;
  font-size: 14px;
  color: #101828;
}
.kv-ve__ai-loading-subtitle {
  font-weight: 500;
  font-size: 12px;
  color: #667085;
}

@keyframes kvVeSpin {
  to { transform: rotate(360deg); }
}

/* Transition */
.kv-ve-fade-enter-active,
.kv-ve-fade-leave-active {
  transition: opacity 0.16s ease;
}
.kv-ve-fade-enter,
.kv-ve-fade-leave-to {
  opacity: 0;
}

/* AI commander (Blocks sidebar) */
.kv-ve__ai-commander {
  position: relative;
  padding: 2px;
  border-radius: 14px;
  margin: 12px 8px 14px;
  background: linear-gradient(
    83deg,
    #F49E8A 0%,
    #E83E76 25%,
    #936CAD 50%,
    #3FAED5 75%,
    #F49E8A 100%
  );
  background-size: 200% 200%;
}
.kv-ve__ai-commander-inner {
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(234, 236, 240, 0.95);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 10px;
}
.kv-ve__ai-commander-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 12px;
  color: #101828;
  margin-bottom: 6px;
}
.kv-ve__ai-commander-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 8px;
  color: #fff;
  font-weight: 900;
  font-size: 11px;
  letter-spacing: 0.02em;
  background: linear-gradient(
    83deg,
    #F49E8A 0%,
    #E83E76 25%,
    #936CAD 50%,
    #3FAED5 75%,
    #F49E8A 100%
  );
  background-size: 200% 200%;
}
.kv-ve__ai-commander-hint {
  font-size: 12px;
  color: #667085;
  margin-bottom: 8px;
  line-height: 1.35;
}
.kv-ve__ai-textarea {
  width: 100%;
  min-height: 150px;
  max-height: 500px;
  resize: none;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.35;
  outline: none;
  overflow: auto;
  background: #fff;
}
.kv-ve__ai-textarea:focus {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}
.kv-ve__ai-commander-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
.kv-ve__ai-btn {
  width: 100%;
  border-radius: 12px !important;
  padding: 10px 12px !important;
  font-weight: 500;
}
</style>


